/**
 * The vocabulary of a reasoning graph.
 *
 * Every field here exists because something on screen reads it. `confidence`
 * sets strand thickness, `novelty` sets a node's glow, `tension` bends colour
 * toward the contradiction hue, `terminal` stops a strand dead. Nothing is
 * carried for decoration.
 */

import type { Effort, ProviderId } from './models'
// A value import, and safe as one: `models.ts` imports nothing at runtime, so
// this edge cannot close a cycle. It is here for `checksSkippedLine`.
import { fmtUsd } from './models'
import type { LinkReading } from './link'
import type { ResearchState } from './research'
import type { ResearchActionContract } from './researchContract'
import { doiOf, registrableHost } from './url'
import { SYNDICATION_HAMMING_MAX, foldText, hamming, textHash } from './similarity'
// The status words: one table, re-exported here so the many modules that
// have always imported a reading or a bearing label from `types` keep doing so.
// A runtime edge types → vocabulary → models, safe for the reason the `fmtUsd`
// import above is: `vocabulary.ts` imports nothing at runtime but `models.ts`,
// which imports nothing at runtime, and it takes this module's types as
// `import type` only — so the edge cannot close a cycle.
import {
  BEARING_LABEL,
  LINK_READING_LABEL,
  READING_LABEL,
  STATUS_WORD,
  conjecturesProposed,
} from './vocabulary'
export { BEARING_LABEL, LINK_READING_LABEL, READING_LABEL }
// Type-only, and it has to stay that way. `verbatimModuleSyntax` erases this
// entirely, so it creates no runtime edge from core into engine — which matters
// here, because this module sits close to the boot cycle `models.ts` documents.
import type { ModelError } from '../engine/providers/contract'
// Type-only for the reason `LinkReading` is: the move list is a runtime table in
// `conjecture.ts`, and this module must not grow a runtime edge into it.
import type { ConjectureMove } from './conjecture'
import type { PriorArtReading } from './priorArt'
import type { SweepKind } from './sweep'
export type { LinkReading }
export type { ConjectureMove }
// ↑ TYPE-ONLY on purpose. `verbatimModuleSyntax` erases the line, so this module
//   stays free of runtime imports and the codec never validates a stored
//   document against a table a later release is free to edit. `LinkReading` is
//   imported rather than restated for the same reason the codec is driven off
//   `PROVIDERS`: a sixth reading added in `link.ts` has to reach the stored
//   record, and a hand-copied union is exactly what fails to.

/** What kind of reasoning move a node represents. */
export type NodeKind =
  | 'seed' // the idea under examination
  | 'mechanism' // a proposed how — the load-bearing middle of an argument
  | 'evidence' // something observed or established that bears on the claim
  | 'assumption' // an unstated premise made explicit
  | 'implication' // what follows if the parent holds
  | 'contradiction' // a reason the parent cannot hold
  | 'gap' // a known unknown blocking progress
  | 'analogy' // a structure borrowed from another domain
  | 'experiment' // a concrete test that would move confidence
  | 'insight' // a non-obvious conclusion worth keeping
  | 'synthesis' // a bridge fusing two distant branches
  | 'conjecture' // a proposal the model recalls no source stating, with a prediction that separates it

/**
 * The semantic channels colour is allowed to encode. Seven, and one is new.
 *
 * `refuted` earns a hue because "the world says no" — a source that states the
 * opposite, or an observation that went against the claim — is the most valuable
 * sentence this tool can produce, and nothing here can carry it. `tension`
 * already means "the model argued against its parent", which is a different
 * authority entirely: a contradiction is the graph arguing with itself.
 *
 * There is deliberately NO `contested` value. A node three passes could not
 * agree about is still a mechanism or a contradiction, and overwriting its hue
 * would delete the more useful of the two facts. Divergence is drawn as FORM —
 * one strand rendered as two that never rejoin — and read from `panelSplit()`.
 */
export type Channel = 'signal' | 'weak' | 'tension' | 'gap' | 'insight' | 'refuted' | 'dead'

/* ── what a source check found ────────────────────────────────────────────
   Everything in this block describes a RECORD — what a search returned on a
   date — and never a truth. The naming is part of the guarantee. */

/**
 * What a source check can honestly conclude. Six, and no more.
 *
 * Named `SourceReading` rather than `Verdict` on purpose: a tool that must not
 * adjudicate should not have a field called `verdict`, and the name is most of
 * what stands between this and a future panel that renders a judgement.
 *
 * `nothing-found` is NOT "false" — the search ran, results came back, and none
 * of them addressed this. `not-checkable` exists because without it a
 * derivation step ("the integral diverges") is forced into `nothing-found`,
 * which the prior-art half would then read as originality: a discovery
 * manufactured out of a category error. `unsourced` is its own value rather
 * than a flag on `nothing-found` because the two are opposite findings and must
 * never share a label, a note, or a count.
 *
 * The gate lands a second case on `not-checkable`: an absence beside a search
 * that partly failed, which is not evidence of anything. That one IS a flag
 * (`Verification.demotedFor`), because the two are one non-finding for two
 * reasons and share every count, so the label is worded to be true of both and
 * the note says which (D326).
 */
export type SourceReading =
  | 'supported' // the sources received address the claim and bear it out
  | 'mixed' // the sources received address it and disagree
  | 'refuted' // a page the model OPENED states the opposite
  | 'nothing-found' // the search returned results and none of them address it
  | 'not-checkable' // sources cannot settle it, or an absence beside a failed search
  | 'unsourced' // every source it named was missing from the search results

/**
 * Which evidence surface a check actually had.
 *
 * Set by the caller from what it declared and what it inserted — never by the
 * model, which cannot know whether the user's own documents were in its prompt.
 */
export type VerifyScope = 'web' | 'corpus' | 'both'

/**
 * How a source bears on the claim, as the model reads it.
 *
 * `context` is the escape hatch that stops a relevant-but-neutral source being
 * forced onto a side — which is how `mixed` becomes `refuted` by accident.
 */
export type Stance = 'supports' | 'disputes' | 'context'

export interface WebSource {
  textRetentionVersion?: 1
  textTruncations?: import('./textRetention').TextTruncation[]
  /** Exactly as the search returned it. The link the inspector renders. */
  url: string
  /**
   * From the API response, NEVER from the model.
   *
   * The citation gate protects the URL; the title is what carries a source's
   * authority to a reader. A real arxiv.org link under a paraphrased title is a
   * citation the gate lets through and the reader cannot check.
   */
  title: string
  /** Bounded text the model copied. Rendered in quotation marks only
   *  when `quoteFound === true` — the marks assert a verbatim reading. */
  quote: string
  stance: Stance
  /**
   * The check gave this source no stance, or one outside the three, and `stance`
   * reads `context` in its place — as the live pass and the codec have always
   * filled it, so no gate and no reader ever sees a side the check did not give.
   *
   * Set only where that filling happened, and read by one thing: the stance
   * count (`stanceTally`), which says "not labelled" for these rather than
   * counting them as sources the check read as context. A count that folded the
   * two together would claim a label nobody gave. Absent on every source whose
   * stance was given, and on the ones an older build filled in before this was
   * recorded, which read as context because that is all their record says.
   */
  stanceUnlabelled?: true
  /** `fetch` means the model opened the page. "A paper with this title exists"
   *  and "this paper says X" are not the same claim and must not read as one. */
  via: 'search' | 'fetch'
  /**
   * Substring check of the quote against the fetched document text.
   *
   * THREE states, and the third is why this is optional. `undefined` means the
   * check was impossible — a search snippet, or a fetch whose document arrived
   * as a base64 PDF. `false` there would accuse the model of inventing a quote
   * it copied correctly, which is this whole feature pointed at its own user.
   */
  quoteFound?: boolean
  /**
   * True only when the complete fetched text, rather than a truncated prefix,
   * supplied the signatures below. Absence is unknown for legacy records and
   * must never satisfy a full-text protocol requirement.
   */
  textComplete?: boolean
  /**
   * The page's own text, as two signatures, for `refutingDomains` to collapse on.
   *
   * Set ONLY where the page arrived with text, and taken over the SAME folded
   * string the quote check read — so the record cannot carry a hash of text
   * nobody compared. Absent everywhere else, and absent means "no signature":
   * a source with no signature merges with nothing.
   *
   * Machine fields rather than displayed ones. They are stored because the disk
   * gate has to reach the same verdict as the live one from the record alone
   * (D13), and the text they were taken over is gone by then — it lives for the
   * duration of one pass and is never written. See `core/similarity.ts` and
   * `docs/decisions.md` D28.
   */
  textHash?: string
  /** Locality-sensitive, so a copy under different boilerplate lands a few bits
   *  away rather than somewhere else entirely. Absent below
   *  `SIMHASH_MIN_CHARS`, where no signature is stable enough to act on. */
  simHash?: string
  /**
   * A second reading of the quote, by the judge, beside the model's `stance`.
   *
   * `stance` is the reasoning model's own say-so and the one judgment in a check
   * nothing mechanical tests: `reconcile` checks that the quote is on the page,
   * not what it says. This is the same question put to a non-generating reader
   * over the same words, stored with the probability it put on each answer and
   * the model that answered. It is an ANNOTATION: `refutedIsEarned` never reads
   * it, no reading moves on it, and the inspector prints it only where the two
   * disagree. `neither` is the judge's word for `context`. See D106.
   */
  judged?: {
    stance: 'supports' | 'disputes' | 'neither'
    probabilities: { supports: number; disputes: number; neither: number }
    model: ModelStamp
  }
  /**
   * What kind of evidence this source is — a trial, a review, a news report of
   * either — as the check that admitted it read the page.
   *
   * Four people in a study asked for it in their own words ("primary paper or
   * coverage?"), and the card's report count could not say it: five reports of
   * one study read "5 reports · 1 independent" whether the five were five papers
   * or one paper and four write-ups. It is the model's label on a page the gate
   * already admitted, so it is an ANNOTATION, the judge's stance's kind (D106):
   * `refutedIsEarned` never reads it, no reading moves on it, and the Lab's
   * `studyDesign` — which its evidence rules DO gate on — is never filled from it.
   * Absent on every check made before it was asked, and wherever the model could
   * not tell (`unclear` is never stored).
   */
  design?: SourceDesign
}

/**
 * The kinds of evidence a check can name a source as. A value, because the codec
 * validates a stored label against it and the schema offers it to the model; the
 * words a surface prints for each are `DESIGN_WORD` in `core/vocabulary.ts`.
 */
export const SOURCE_DESIGNS = [
  'trial',
  'observational',
  'review',
  'model',
  'primary',
  'news',
] as const

export type SourceDesign = (typeof SOURCE_DESIGNS)[number]

/**
 * A publisher's own notice on a cited work, as Crossref's `update-to` relations
 * report it.
 *
 * TWO members, closed. `retracted` is the work being withdrawn — a retraction, a
 * withdrawal or a removal — and it is the only one this app says out loud.
 * `flagged` is any other update pointing at the record: a correction, an erratum,
 * a partial retraction, an expression of concern. Those are context rather than a
 * verdict, so `flagged` travels as data and is never rendered as one — see D31.
 *
 * Counted, never judged: the classification is a lookup in somebody else's
 * metadata, and no model is asked what a notice means.
 */
export type DocNotice = 'retracted' | 'flagged'

/** One passage of one of the user's own documents, as cited by a check. */
export interface PassageCite {
  textRetentionVersion?: 1
  textTruncations?: import('./textRetention').TextTruncation[]
  docId: string
  /** The title at the time of the check. The document may since be gone. */
  docTitle: string
  /** Document-level passage index, 1-based. Never the call-local number the
   *  prompt printed — this is the durable reference. */
  passage: number
  /**
   * The passage's opening, sliced BY THE CLIENT from the text it printed.
   *
   * Never accepted from the model: the client has the passage in hand, so a
   * model-supplied quote here would be an unchecked assertion about text we
   * could have quoted ourselves. It is also what makes deleting a document
   * non-destructive — removal costs the link, not the record.
   */
  quote: string
  /**
   * The notice on the work this passage came out of, copied from the document at
   * the moment of the check.
   *
   * STRUCTURAL, and that is the whole point of the field. `liveSources.ts` also
   * writes ' — RETRACTED' into the doc TITLE, because a title travels everywhere
   * a cite is shown — but a title is prose, and a coach or an inspector that
   * decided a paper was retracted by searching that prose for a word would fire
   * on the retraction NOTICE itself, whose own title says the same thing. Only a
   * record this app fetched can carry one: a document you pasted has no DOI to
   * look one up by. See `docs/decisions.md` D31.
   */
  notice?: DocNotice
}

/** What a check read out of the user's own documents. It rides inside the
 *  verification record: a citation with no check to belong to is a fact about
 *  nothing. */
export interface CorpusEvidence {
  cites: PassageCite[]
  /** Passage numbers cited that the retrieval never printed. Displayed, never
   *  swallowed — a hidden drop is indistinguishable from a check that had
   *  nothing to say. */
  dropped: number
  match: 'none' | 'thin' | 'ok'
  /** Passages offered, and the best term coverage among them. Raw, both: the
   *  panel must never round 0.31 into "weak". */
  offered: number
  bestCoverage: number
  /** Corpus size when the check ran. A check against 3 documents is not the
   *  same check as one against 12, and a re-run has to be able to say so. */
  docsAtCheck: number
}

/** A source that already states, or already does, the specific thing. */
export interface PriorArt {
  /** `canonicalUrl(url)` of a surviving web source, or `passageRef(cite)` of a
   *  surviving passage cite. Keyed rather than indexed because reconciliation
   *  deletes sources and an index would then point at a different one. */
  ref: string
  covers: string
  closeness: 'same' | 'overlapping' | 'adjacent'
}

/**
 * The prior-art ref for one cited passage: its document and its DOCUMENT-level
 * number, the two fields the cite itself stores.
 *
 * Never `p12`. The model names a passage by the call-local number the prompt
 * printed, and that number meant nothing once the call was over — the codec
 * rebuilt the ref from the cite's document-level number, so a prior-art entry
 * naming call-local `p3` was dropped on reload, or re-attached to whichever cite
 * happened to be the third passage of its own document. And a document-level
 * number alone repeats across documents: two cited documents can each have a
 * passage 3. `reason.ts` translates the model's ref into this one, `persist.ts`
 * rebuilds the admitted set from it, and `priorWork.ts` titles by it, so all three
 * read one string. See D146.
 *
 * The `passage:` prefix keeps it clear of every web ref, which is a canonical
 * http(s) URL. Nothing parses it — readers compare it whole — and it still names
 * one pair for a docId carrying a `#` of its own, because the passage is what
 * follows the last one and a passage number never contains one.
 */
export const passageRef = (c: Pick<PassageCite, 'docId' | 'passage'>): string =>
  `passage:${c.docId}#${c.passage}`

/* ── provenance: which model produced a record ─────────────────────────────
   In a single-model app "the model said X" is unambiguous. In this one it is
   not: `refuted` is quote-backed on one provider and opened-page-backed on the
   other, and `confidence`/`novelty`/`tension` are each model's own self-ratings
   on its own scale. So a record that cannot name its author is a record whose
   evidence cannot be weighed. */

/**
 * Which model produced a record, copied at the moment it ran.
 *
 * A COPY, not a pointer into the registry — the same decision `ForeignRef`
 * makes, for the same reason. Models get delisted; a record made with one does
 * not stop having been made with it. Resolving a stored id against the live
 * registry would erase the provenance of every record made with a retired
 * model, which is this design's one guarantee defeated by a table edit.
 *
 * `name` as well as `id` because `id` is what the API answers to and `name` is
 * what a reader recognises — and after a delisting there is nothing left to look
 * the second one up in.
 */
export interface ModelStamp {
  provider: ProviderId
  /** The API's own model id, e.g. `claude-opus-5`. Whatever answered. */
  id: string
  /** The display name at the time, e.g. `Claude Opus 5`. */
  name: string
}

/**
 * What a model from another provider made of a claim.
 *
 * The panel's admitted ceiling is that three passes of one model converging is
 * one model converging: the seats share training data, so their independent
 * agreement is inflated by an amount nothing inside that mechanism can measure.
 * This is the escape — a model from a DIFFERENT provider, holding different
 * priors, asked to break the claim rather than extend it.
 *
 * The finding names the part examined. Missing definitions can prevent a
 * review, and a proposed criticism of relevance does not refute the statement.
 * Historical reviews retain their original verdict without an invented scope.
 */
export interface Audit {
  proofStep?: { obligationId: string; revision: string; stepId: string | null }
  /**
   * The model that examined it. Always from a provider other than the one that
   * produced the claim — the feature has no meaning otherwise, and `runAudit`
   * refuses rather than falling back to the same house.
   */
  model: ModelStamp
  /**
   * `objection` proposes a specific criticism in the declared scope. `pass`
   * records no specific objection; `insufficient-context` leaves work unfinished.
   */
  verdict: 'objection' | 'pass' | 'insufficient-context'
  /** Absent on historical binary reviews; never infer their intended scope. */
  scope?: AuditScope
  /** A model criticism has no authority to refute its target. */
  status?: 'proposed' | 'no-objection' | 'needs-context'
  objectionNodeId?: string
  /** The scoped finding or the information needed to finish the review. */
  note: string
  at: number
}

export type AuditScope =
  'statement' | 'derivation' | 'assumptions' | 'scope' | 'novelty' | 'relevance'

export interface AuditCoverage {
  version: 1
  requested: string[]
  skipped: string[]
  /** A new user-authorized attempt, never a replay or rewrite of its ancestor. */
  continuationOf?: string
  claims: {
    nodeId: string
    callId?: string
    transport: 'not-started' | 'unknown' | 'completed' | 'refused' | 'failed'
    truncated: boolean | null
    structured: 'missing' | 'invalid' | 'valid'
    research: 'incomplete' | 'insufficient-context' | 'completed'
  }[]
}

/**
 * What one pass cost, and which model billed it.
 *
 * Carried by a result AND by a failure, because a pass stopped halfway still ran
 * what it ran — the reason `ModelError.spent` exists at all.
 */
/**
 * What a receipt's aggregates were made of, when the provider said.
 *
 * It began as a DIAGNOSTIC and nothing here was priced: `estimateUsd` charged
 * every input token at the base rate, which put the figure above the bill, and
 * feeding these categories into the money would have narrowed that margin. D311
 * narrowed it on purpose, at the paying user's request: a read named here is
 * priced at the model's read rate and a page open at the model's open rate, so
 * a complete breakdown prices at list. A category NOT named here — an
 * `unclassified` field, or a row with no breakdown at all — is still charged the
 * dearer way, which is what keeps the figure from going under. It still answers
 * the question the aggregate cannot: how much of a run actually reused the
 * prompt cache, and therefore whether a cache change did anything at all.
 *
 * Two identities are load-bearing, and `asSpend` enforces both on the way in:
 *
 *   inputUncached + cacheWrite5m + cacheWrite30m + cacheWrite1h + cacheRead
 *     + inputUnclassified
 *     === inputTokens
 *   webSearches + webFetches + serverRequestsUnclassified === searches
 *
 * The two `unclassified` fields are what make the identities survive contact
 * with reality. A row stored before this existed has no breakdown at all, and a
 * detailed receipt merging into one must not pretend to cover the older half —
 * so the older half arrives as unclassified rather than as a guess. The same
 * field absorbs a provider that reports a total without its split: on this API
 * `cache_creation_input_tokens` is authoritative and `cache_creation`'s 5m/1h
 * breakdown is not always beside it, and on the other API a server tool call
 * cannot be separated into a search and a page open at all.
 */
export interface SpendDetail {
  /** Input the provider billed at the base rate. */
  inputUncached: number
  /** Input written to the five-minute cache, billed at 1.25×. */
  cacheWrite5m: number
  /** Input written to OpenAI's thirty-minute cache, billed at 1.25×. */
  cacheWrite30m: number
  /** Input written to the one-hour cache, billed at 2×. */
  cacheWrite1h: number
  /** Input served from cache, billed at the model's read rate — a tenth of base
   *  on most rows, a twentieth on Opus 5.5. */
  cacheRead: number
  /** Input this app could not attribute to one of the four above. Never a guess
   *  — it is the part of `inputTokens` whose category was not reported. */
  inputUnclassified: number
  /** Server-side searches, billed per request. */
  webSearches: number
  /** Server-side page opens. Free on one provider, which is why they are counted
   *  apart from searches even though `searches` sums them. */
  webFetches: number
  /** Server requests whose kind this app cannot establish. */
  serverRequestsUnclassified: number
}

export interface Spend {
  model: ModelStamp
  inputTokens: number
  outputTokens: number
  /**
   * Search and page-open requests, summed.
   *
   * Summed in the transport rather than by the caller: on one provider opening a
   * page is an action inside the search tool and the two cannot be separated, so
   * a caller adding them itself would be doing arithmetic that is only
   * meaningful on the other. Billed per request and never inside the token
   * counts, which is why it is carried at all.
   */
  searches: number
  /**
   * The breakdown, when one was reported. Optional and NOT defaulted: absence
   * means "recorded before the meter existed", which is a different statement
   * from "every category was zero", and a zero-filled row would erase it.
   */
  detail?: SpendDetail
  /**
   * The largest single request folded into this row, in input tokens (D311).
   *
   * A long-context rate is decided per REQUEST — above 272K input tokens one
   * request is repriced whole — but the ledger keeps one row per model, so a
   * session of short requests on a model with such a tier used to cross the
   * threshold in aggregate and be repriced as though it were one enormous
   * prompt. This is what `estimateUsd` compares against the threshold instead.
   *
   * Stamped where a receipt reaches the ledger (`withPeak`), merged as a maximum
   * (`addSpendRow`), and ABSENT wherever any part of the row is unknown — a row
   * stored before it existed, or merged with one. Absent, the row's own total is
   * compared, which can only reprice upward: the old behaviour, kept exactly
   * where nothing better is known.
   */
  peakInputTokens?: number
}

/**
 * JSON retained at the model-call boundary.
 *
 * Kept here instead of importing the capsule's JSON type because an operation
 * is the durable source and the capsule is only one projection of it. The
 * persistence codec still walks this value defensively before admitting it.
 */
export type ModelCallJsonValue =
  null | boolean | number | string | ModelCallJsonValue[] | { [key: string]: ModelCallJsonValue }

export const MODEL_CALL_RECEIPT_VERSION = 1 as const

export interface ModelCallReceipt {
  version: typeof MODEL_CALL_RECEIPT_VERSION
  id: string
  /** One-based physical-call order inside the logical operation. */
  ordinal: number
  kind: 'structured' | 'text'
  /** Frozen research intent and context actually rendered into this request.
   * Historical calls omit it rather than reconstructing today's context. */
  researchContract?: ResearchActionContract
  /** Exact post-render strings handed to the provider-neutral adapter. */
  messages: readonly [{ role: 'system'; content: string }, { role: 'user'; content: string }]
  /** The provider-neutral request after effort and output ceilings were lowered. */
  parameters: {
    schema: { name: string; value: ModelCallJsonValue } | null
    effort: Effort
    maxTokens: number
    rootArrayKey: string | null
    web: {
      maxSearches: number
      maxFetches: number
      maxContentTokens: number | null
    } | null
    cacheKey: string | null
    maxRetries: number
  }
  /** Text calls retain cache-block boundaries and history without pretending
   * they used a structured-output schema. */
  textContext?: {
    systemDynamic?: string
    systemTail?: string
    history: { role: 'user' | 'assistant'; text: string }[]
    output: string
    internalAttempts: null
  }
  selectedModel: ModelStamp
  /** Null when a failed request never produced an answer whose model was known. */
  answeredModel: ModelStamp | null
  startedAt: number
  completedAt: number
  outcome:
    | { kind: 'completed'; truncated: boolean }
    | { kind: 'refused'; note: string | null; truncated: boolean }
    | { kind: 'failed'; errorKind: ModelError['kind']; reason: string }
  /** Null means the failed request reported no usage, never an invented zero. */
  usage: Spend | null
}

export interface Verification {
  textRetentionVersion?: 1
  textTruncations?: import('./textRetention').TextTruncation[]
  /**
   * The operation that produced this record.
   *
   * Optional only for checks written before evidence history existed. New
   * records carry it so an old pass in the feed can recover its own finding
   * after later checks have landed on the same node.
   */
  opId?: string
  /** The exact node wording this pass searched for. A later rewrite must not
   * make an old check look as though it examined the replacement wording. */
  claimAtCheck?: string
  /** Durable identity of the complete checked wording. The display snapshot may
   * be clipped by the storage codec; this signature keeps that clipping from
   * making the same claim look stale after reload. */
  claimHashAtCheck?: string
  /** The scan label paired with `claimAtCheck`, kept for historical views. */
  labelAtCheck?: string
  /** Highest durable node ordinal present when this check landed. Unlike a wall
   * clock, it cannot move backwards; dependency staleness prefers this marker. */
  nodeOrdinalAtCheck?: number
  reading: SourceReading
  /** What the model said, kept ONLY when reconciliation changed it. The gap
   *  between what it asserted and what its citations could carry is itself the
   *  finding. Absent when it gave no reading: the gate fills a missing one as an
   *  absence to act on, and the fill is not something it said (D322). A record
   *  written before that can carry the fill here, indistinguishable from an
   *  absence the model did report. */
  claimedReading?: SourceReading
  /**
   * Why the reading is the gate's: present ONLY on a `not-checkable` the gate
   * wrote because part of the search failed beside an absence, whether the pass
   * reported that absence or gave no reading at all. An absence beside a failed
   * search is not evidence of anything, and this says so on the record.
   *
   * Stored rather than derived, because since D322 the one case nothing else on
   * the record can tell apart is the pass that gave no reading: it keeps no
   * `claimedReading`, so field for field it reads as a pass that itself called the
   * claim a step sources cannot settle, beside a failed search. The codec keeps
   * it only beside that reading and a failure, and re-derives it on a record
   * from before it from the claimed absence (D326).
   */
  demotedFor?: 'failed-search'
  /** What the sources say, finding first, asked for in at most 70 words (records
   *  from before that ceiling ran longer, and nothing clips them). Never an adjudication.
   *  Absent when `reading === 'unsourced'`: prose whose every citation was
   *  deleted as fabricated is the one part of this record nobody should read. */
  summary?: string
  sources: WebSource[]
  corpus?: CorpusEvidence
  /**
   * The user's documents were searched for this claim and none of their passages
   * was offered to the check — present ONLY on that miss, and absent on a check
   * with no documents to search or one that was offered some.
   *
   * A miss used to leave no trace on the record: `corpus` is set only when
   * something was offered, so a check that searched two documents and found
   * nothing read exactly like one that had none, and with "Prefer my sources" on
   * it fell back to the web without a word. `offered` is always 0 and is carried
   * so the record reads as what it is — N searched, none offered — rather than
   * as a bare count. `preferCorpus` is the switch as the pass read it, because
   * a fallback against the user's stated preference is worth saying so.
   */
  corpusSearched?: { docs: number; offered: 0; preferCorpus: boolean }
  priorArt: PriorArt[]
  /**
   * Novelty re-rated DOWN against prior art. Never up.
   *
   * Admissible only when a `priorArt` entry survived reconciliation and the
   * number is not above the model's own self-rating. An absence of prior art
   * produces no number at all: a search that found nothing is a search that
   * found nothing, and printing 0.75 for it under a field name containing the
   * word "measured" is the tool declaring it has found something.
   *
   * `node.novelty` is never written, so node radius cannot silently come to
   * mean two different things.
   */
  measuredNovelty?: number
  /** One sentence naming which source moved the number. */
  noveltyNote?: string
  scope: VerifyScope
  /** The queries the pass actually issued, read off the API response. This is
   *  what makes `nothing-found` auditable: an absence only means something
   *  next to what was looked for. */
  queries: string[]
  /** How many results those searches returned. `nothing-found` with `seen === 0`
   *  is a failed pass rather than a result, and is abandoned. */
  seen: number
  /** Web sources the model named that the search never returned, deleted. */
  dropped: number
  /** True when the tool loop was still running at the continuation cap. A
   *  partial look, and it says so rather than reading as a finished check. */
  partial?: boolean
  /** One line per search step that errored. "Nothing came back" and "the search
   *  failed" are the same empty list to the code and opposite findings to a
   *  reader. */
  failures?: string[]
  checkedAt: number
  /**
   * Which model searched.
   *
   * REQUIRED, alone among the four stamps: a check with no model behind it is
   * not a check. `refuted` is quote-backed on one provider and
   * opened-page-backed on the other, so a reading whose model cannot be named is
   * a reading whose evidence cannot be weighed. Safe to require — the demo has
   * no verification by construction and never will, and the v2→v3 migration
   * fills every stored one.
   */
  model: ModelStamp
  /**
   * Whether a quote COULD have been checked against its page on this pass.
   *
   * Written from what the ADAPTER actually received, never from the registry's
   * assertion. The registry answers this question before the call, for copy on a
   * button; this answers it after, for the record. If the two ever disagree,
   * this one is right.
   *
   * Stored rather than derived, and this is the one place in the app where that
   * is the honest choice: it cannot be recovered from the registry (the model may
   * be delisted) and it cannot be recovered from `sources`, because every
   * `quoteFound` being `undefined` is ambiguous between "no page was opened" and
   * "pages were opened and this provider does not return their text".
   *
   * ABSENT on a pass with no web surface at all. A documents-only check has no
   * page to check a quote against, and `false` there would report a model limit
   * that was never reached.
   */
  quotesCheckable?: boolean
  /**
   * Why the judge's second reading is missing from a check that had a judge to
   * ask: the call failed, and this is its kind in a sentence. Absent when no
   * judge was configured — that is the ordinary state and needs no note — and
   * absent when every quoted source carries a `judged` reading.
   */
  judgeNote?: string
}

/**
 * One explicit rebuild against one dated source record.
 *
 * The earlier branch is not deleted: it remains the "before" side of the
 * comparison and its stale strands stay visibly detached. This record names the
 * new heads, so the source card can distinguish "needs a second look" from
 * "rebased; prior version retained" without asking a model to judge either.
 */
export interface EvidenceRebase {
  verificationAt: number
  /** Exact check operation this rebuild reviewed, when the event has one. */
  verificationOpId?: string
  /** Landing position in the node's append-only check stream. This remains
   * stable for checks written before operation ids existed. */
  verificationEventIndex?: number
  opId: string
  rebasedAt: number
  /** Claims in the dependency closure when the user started the rebuild. */
  affected: string[]
  /** New branch heads the rebase pass authored. */
  produced: string[]
}

/** A source-check event whose claim may no longer be on the graph.
 *
 * Pruning and undo can remove a node, but they cannot make a paid search cease
 * to have happened. The live card owns events while the claim exists; this
 * session archive is the other half of the same append-only ledger. */
export interface VerificationArchiveEntry {
  nodeId: string
  /** Needed to re-apply the measured-novelty gate after the node is gone. */
  noveltyAtCheck: number
  verification: Verification
}

/* ── the survey of the field ─────────────────────────────────────────────
   The one search that looks at the QUESTION rather than at a claim derived from
   it. It carries no reading: a question has no truth value for a source to bear
   on, so nothing here can be supported, refuted or tainted, and nothing that
   gates, stops or colours the graph reads it. It is a dated record of what one
   bounded search returned, and every later pass is handed it as exactly that. */

/** Which of the survey's five questions a row answers. See `core/survey.ts`. */
export type SurveyPart = 'evidence' | 'landmark' | 'tried' | 'disagreement' | 'untested'

/** One sentence of a survey, and the returned sources it rests on. */
export interface SurveyRow {
  part: SurveyPart
  /** The model's sentence about what the returned sources report. */
  text: string
  /** The kind of study behind it — a meta-analysis, a cohort, a trial. Absent
   *  where the model named none, which an `untested` row never does. */
  design?: string
  /**
   * `canonicalUrl` of the admitted sources this row rests on, never an index:
   * the gate deletes sources, and an index would then point at a different one.
   * Empty only on an `untested` row — an absence in what came back has no page
   * to point at, and every other part is set aside without one.
   */
  refs: string[]
}

/** A source a survey rests on. The identity the SEARCH returned — URL, title,
 *  whether the page was opened — and nothing the model wrote about it. */
export interface SurveySource {
  url: string
  /** From the API response, never from the model, for `WebSource.title`'s reason. */
  title: string
  via: 'search' | 'fetch'
}

/**
 * What a search returned about the seed question: the bodies of evidence and
 * the kind of study behind each, landmark results, what has been tried, where
 * results part ways, and what nothing in the results tested.
 *
 * Carried by the seed alone, as `verification` is by a claim, and admitted
 * through the same citation gate: a row rests only on URLs the search actually
 * returned, and the count set aside is kept rather than swallowed.
 */
export interface FieldSurvey {
  textRetentionVersion?: 1
  textTruncations?: import('./textRetention').TextTruncation[]
  /** The operation that produced it. Required: there are no legacy surveys. */
  opId: string
  /** The seed wording this pass searched around, and its durable identity —
   *  `claimAtCheck`'s pair, for `verificationLive`'s reason. */
  questionAtSurvey: string
  questionHashAtSurvey: string
  /** The model's prose, finding first. Absent when every source it named was set
   *  aside: prose with no returned source behind it is the part nobody should read. */
  summary?: string
  rows: SurveyRow[]
  sources: SurveySource[]
  /** The queries the pass issued, read off the API response. */
  queries: string[]
  /** How many searches the response recorded — `queries` is bounded on disk. */
  searches: number
  /** How many results those searches returned. */
  seen: number
  /** Distinct URLs the model named that the search never returned, deleted. */
  dropped: number
  /** Rows set aside because not one returned source stood behind them. */
  rowsSetAside: number
  /** The tool loop was still running at its cap — a partial look, said so. */
  partial?: boolean
  /** One line per search step that errored. */
  failures?: string[]
  surveyedAt: number
  /** Which model searched. Required, for `Verification.model`'s reason. */
  model: ModelStamp
}

/* ── the adversarial panel ───────────────────────────────────────────── */

/**
 * Where one panel seat landed on the target claim.
 *
 * Four values. Without `holds-weaker`, a pass that found a real unstated
 * condition must choose between hiding it (`holds`) and overstating it
 * (`fails`), and that missing bucket is what makes a panel read as unanimous
 * when it is not. Beyond four, agreement — which is exact equality — starts
 * splitting on distinctions that are not disagreements.
 *
 * None of these says whether the claim is TRUE. They are positions on whether
 * it follows, under one discipline, from the branch above it.
 */
export type PanelVerdict = 'holds' | 'holds-weaker' | 'fails' | 'undecidable'

export interface PanelVote {
  lens: Lens
  verdict: PanelVerdict
  /** One or two sentences, the pass's own words, stored verbatim. This is the
   *  finding — the retained trace is clipped on the way to disk and the panel's
   *  substance must not depend on surviving that. */
  because: string
  /** This pass's confidence in its OWN verdict. Shown per pass and never
   *  averaged: an average of three verdicts is a fourth opinion wearing the
   *  costume of a measurement. */
  confidence: number
}

export interface PanelCheck {
  /** The lenses asked, in order. Longer than `votes` when a pass failed —
   *  "three asked, two returned" is information the display has to state. */
  lenses: Lens[]
  votes: PanelVote[]
  /** Held fixed across every pass, and recorded because holding it fixed is
   *  what makes a disagreement attributable to the lens. */
  temper: Temper
  /** Drives the split animation the way `foldAt` drives the chevrons: a pure
   *  function of now − checkedAt, with no accumulator. */
  checkedAt: number
  /**
   * The model all three seats ran on, resolved once before seat one.
   *
   * Singular and not per-vote, and that is a decision rather than a saving: the
   * feature's whole claim is that a disagreement is attributable to the LENS, and
   * three seats on three models cannot support it. Taken beside `temper` and for
   * exactly the same reason.
   *
   * Optional for one case only — the demo's two hand-written panels, which nobody
   * ran. `PanelVote` gains nothing.
   */
  model?: ModelStamp
}

/* ── the experiment loop ─────────────────────────────────────────────── */

/**
 * Something that actually happened, recorded against a node.
 *
 * The only field on a ReasonNode that is not the model's, and the only one in
 * the app that cannot be re-obtained by paying for another pass. A graph can be
 * re-reasoned; a run at 4 K for sixty hours cannot. Every decision below follows
 * from that.
 *
 * It is NOT a claim replacement. `editedAt` means "this wording is yours and the
 * model's is gone"; an outcome leaves the claim exactly as written and puts a
 * fact beside it that the claim now has to live with.
 */
export interface Outcome {
  /** What was observed, in the user's words. Never paraphrased, never
   *  regenerated, passed to the model verbatim — softening "did not appear
   *  above a 0.2 nN noise floor" into "was not observed" manufactures a
   *  stronger result than anyone obtained. */
  observed: string
  /** How the observation bears on the claim — deliberately not whether the
   *  claim is true. The vocabulary is directional rather than verdictive. */
  bearing: 'supported' | 'refuted' | 'inconclusive'
  /** When the result was obtained. Separate from `recordedAt` because results
   *  are written up late, and the export's credibility rests on the
   *  observation's own date. */
  at: number
  /** When it was entered. THIS — not `at` — is what a descendant is compared
   *  against to decide whether it predates the result, so it must never be
   *  forward-dated: a future `recordedAt` marks every node below it as
   *  predating the result forever, with nothing on screen to explain why. */
  recordedAt: number
  /** Exact proposition this observation was recorded against. Optional only on
   *  documents written before outcome snapshots existed. */
  claimAtRecord?: string
  /** The node's confidence immediately before the FIRST record on this node.
   *  Written once and never touched by an edit — an edit that refreshed it
   *  would print "0.05 → 0.05" and delete the only number in the sentence that
   *  came from the model. Equal to the current value means unchanged, which is
   *  the normal case: the tool suggests no number. */
  confidenceBefore: number
  /** Where it came from, if named. Free text: an outcome cites a lab notebook
   *  far more often than a PDF, and a typed field would push people toward the
   *  wrong kind of provenance. */
  source?: string
}

/* ── cross-exploration synthesis ─────────────────────────────────────── */

/**
 * A node in another stored exploration, copied at the moment it was used.
 *
 * A copy rather than a pointer, deliberately: the exploration it names can be
 * evicted by the storage budget, forgotten, or written by a schema this build
 * refuses — and a candidate that then renders "(unknown) × (unknown)" is worse
 * than a stale label. One type, two users: a candidate's far end, and a realised
 * node's provenance. They are the same fact at two moments.
 */
export interface ForeignRef {
  sessionId: string
  /** The exploration's seed. What names the source once the document is gone. */
  seed: string
  nodeId: string
  label: string
  /** The far side has to be judgeable without leaving the exploration you are
   *  in, and it is what the realisation prompt hands the model. */
  claim: string
  /** When this copy was taken. */
  at: number
}

/* ── conjectures ─────────────────────────────────────────────────────────
   A proposal the model recalls no source stating, carried as a record the rest
   of the app can test rather than as a word it can print. Nothing in this block
   says a conjecture is new: whether anyone has said it is measured afterwards,
   by a search, and never by the pass that proposed it.

   Not `ConjecturePackage` in `core/mathematics.ts`, which is a formal statement
   carrying proof obligations. The two share a word and nothing else: a graph
   conjecture is an empirical proposal with a prediction, a package is a
   theorem-shaped statement, and where the two meet it is a package written to
   state a graph conjecture formally — the package refers to the conjecture,
   never the other way. No type here is named `ConjecturePackage…`, so neither can
   be imported in the other's place. */

/**
 * The observation that separates a conjecture from what it departs from.
 *
 * Four fields rather than one sentence, because the separation IS the check:
 * `expected` and `consensusExpects` are compared on admission, and a prediction
 * both sides would make is not a prediction of this conjecture.
 */
export interface ConjecturePrediction {
  /** The circumstances under which to look. */
  condition: string
  /** What is measured or observed. */
  observable: string
  /** What this conjecture says the observation will show. */
  expected: string
  /** What the expectation in `departsFrom` says it will show instead. */
  consensusExpects: string
}

/**
 * One registration of a conjecture's wording: the date it was set down and a
 * hash of the claim and prediction it was set down with.
 *
 * A registration that silently survived an edit would let a prediction be
 * rewritten after its result came in, so an edit to either re-registers and the
 * earlier stamp is kept rather than overwritten.
 */
export interface ConjectureRegistration {
  /** Epoch milliseconds. */
  at: number
  /** `textHash` over the folded claim and prediction — see `registrationHash`. */
  contentHash: string
}

/** What makes a node a conjecture, beside the fields every node carries. */
export interface ConjectureRecord {
  /** How it was generated. Makes the method visible and lets diversity be counted. */
  move: ConjectureMove
  /** The expectation a specialist would hold instead, stated as a checkable claim. */
  departsFrom: string
  /** The one step the proposing model recalls no source taking. */
  leap: string
  prediction: ConjecturePrediction
  /** The cheapest observation that would kill it, with a rough cost. */
  killTest: string
  /** Node ids from the prompt that it claims to explain. Resolved on landing:
   *  an id the graph does not hold was dropped and counted then. */
  anchors: string[]
  /** Where the proposing model would search first to show it is NOT new. Leads
   *  for the prior-art search, added to its own queries, never replacing them. */
  priorArtLeads: string[]
  /** The wording as it now stands. Always the latest of `registrations`. */
  registered: ConjectureRegistration
  /** Earlier registrations, oldest first, when the claim or prediction was
   *  rewritten. Absence and an empty array mean the same thing. */
  registrations?: ConjectureRegistration[]
  /**
   * Every prior-art search run against it, oldest first, the last few kept.
   *
   * Each names the registration it searched, so a search of wording since
   * rewritten reads as a search of earlier wording rather than of this one.
   * Its reading and `measuredNovelty` are re-derived from its own entries on
   * every load — see `core/priorArt.ts`.
   */
  priorArt?: PriorArtSearch[]
}

/**
 * One page a prior-art search listed as stating some or all of a conjecture.
 *
 * Admitted only if the search returned its URL; the title is the API's and never
 * the model's. `quoteFound` is the tri-state the source check uses: absent where
 * the page's own text never came back to check against.
 */
export interface PriorArtEntry {
  url: string
  title: string
  /** Text the model says it copied from the page. */
  quote: string
  /** `same` takes this conjecture's leap or makes its prediction for its
   *  reason; `overlapping` states part; `adjacent` is the field, not the claim. */
  closeness: 'same' | 'overlapping' | 'adjacent'
  /** One sentence: what of the conjecture this page covers. */
  covers: string
  /** The year the page states, and only where it states one. */
  year?: number
  via: 'search' | 'fetch'
  quoteFound?: boolean
}

/**
 * A returned page the backstop found close by term overlap — carrying enough of
 * the leap's distinctive terms AND of the prediction's to count as addressing
 * both, whether or not the model listed it. Two shares, never a score.
 */
export interface PriorArtBackstopHit {
  url: string
  title: string
  via: 'search' | 'fetch'
  /** Share of the leap's distinctive terms the page carries, 0 to 1. */
  leap: number
  /** Share of the prediction's distinctive terms the page carries, 0 to 1. */
  prediction: number
}

/**
 * One prior-art search: a searched pass that assumed the conjecture had been
 * said before and went looking for where, and what the client read off it.
 *
 * Nothing here says a conjecture is new. The best a search can do is fail to
 * find it stated, and `reading` says exactly that and no more.
 */
export interface PriorArtSearch {
  /** Derived by the client, never claimed by the model — see `core/priorArt.ts`. */
  reading: PriorArtReading
  entries: PriorArtEntry[]
  /** Every distinct returned page was compared; `hits` are the ones close by
   *  term overlap, the first few kept, and `close` how many there were. */
  backstop: { pages: number; close: number; hits: PriorArtBackstopHit[] }
  /** One or two sentences from the searching model, reporting and not ruling. */
  summary?: string
  /** The queries the pass actually issued, read off the API response. */
  queries: string[]
  /** Distinct pages the search returned. */
  seen: number
  /** Pages the model named that the search never returned, deleted unread. */
  dropped: number
  /** One line per search step that failed. */
  failures?: string[]
  /** The tool loop was cut off, or the response stopped at its output cap. */
  partial?: boolean
  /** Novelty re-read DOWN against a surviving `same` or `overlapping` page, never
   *  above the conjecture's own rating. Absent when nothing close survived. */
  measuredNovelty?: number
  /** Whether the provider returned page text a quote could be checked against. */
  quotesCheckable?: boolean
  searchedAt: number
  model: ModelStamp
  /** `registered.contentHash` when the search ran: the wording it searched for. */
  contentHash: string
  /** Fingerprint of the instructions it ran under. Recall is disclosed per
   *  instrument, so a changed prompt is a search whose recall is unmeasured. */
  instrument: string
  opId?: string
}

/**
 * One unsettled point a field sweep reported: one returned page, quoted.
 *
 * `id` is stable (`${sweep.id}.${n}`), and it is what a conjecture's anchor
 * stores when it offers to explain the item. `quoteFound` is `true` or absent,
 * never `false`: an item whose quote is not on the page the search returned is
 * deleted and counted, because an anomaly nobody said is the worst thing a
 * conjecture pass could be handed.
 */
export interface SweepItem {
  id: string
  kind: SweepKind
  /** The searching model's one sentence, meant never to go past the quote. */
  statement: string
  url: string
  /** From the API response, never the model's. */
  title: string
  quote: string
  /** The year the page states, and only where it states one. */
  year?: number
  via: 'search' | 'fetch'
  quoteFound?: true
}

/**
 * One field sweep: a searched pass that reported where the literature on one
 * node's subject is unsettled, in the words of pages it returned.
 *
 * A record of a search, never a statement about the field: an empty sweep is a
 * search that came back with nothing it could quote as unsettled, not a field
 * that has none.
 */
export interface FieldSweep {
  id: string
  /** The node whose subject was swept. */
  nodeId: string
  /** That subject as it read when swept: the node's label and claim. */
  subjectAtSweep: string
  items: SweepItem[]
  queries: string[]
  /** Distinct pages the search returned. */
  seen: number
  /** Items naming a page the search never returned, deleted unread. */
  dropped: number
  /** Items whose quote is not on the page this app received, deleted. */
  unquoted: number
  /** Items missing a kind, a statement or a quote, or resting on a page an
   *  earlier item already rests on, deleted. */
  malformed: number
  /** Items past the sweep's ceiling, dropped by position. */
  overCeiling: number
  failures?: string[]
  /** The tool loop was cut off, or the response stopped at its output cap. */
  partial?: boolean
  quotesCheckable?: boolean
  searchedAt: number
  model: ModelStamp
  /** Fingerprint of the instructions it ran under. */
  instrument: string
  opId?: string
}

/**
 * How a conjecture pass's response was admitted, counted.
 *
 * Every rejection is a paid-for proposal that did not land, so it is counted on
 * the op rather than swallowed — the same shape as the citation gate's `dropped`.
 */
export interface ConjectureAdmission {
  /** Conjectures the response offered. */
  returned: number
  /** Conjectures that landed as nodes. */
  landed: number
  /** Missing a field the contract requires. */
  incomplete: number
  /** A prediction the expectation it departs from would make too. */
  undiscriminating: number
  /** A restatement of a conjecture already on the graph, or of one earlier in
   *  the same response. */
  duplicate: number
  /** Past the pass's ceiling, dropped by position. */
  overCeiling: number
  /** Anchor ids that did not resolve to a node, dropped from landed conjectures. */
  anchorsDropped: number
}

/**
 * Detail added to a compact node when that node is developed later.
 *
 * The kernel on `ReasonNode.reasoning` is never replaced. An elaboration may be
 * authored by a different model in a later portfolio pass, so its provenance is
 * complete in its own right rather than inherited from the node.
 */
export interface NodeElaboration {
  textRetentionVersion?: 1
  textTruncations?: import('./textRetention').TextTruncation[]
  reasoning: string
  nextSteps: string[]
  model: ModelStamp
  opId: string
  authoredAt: number
}

export interface ReasonNode {
  textRetentionVersion?: 1
  /** Client field losses, separate from the provider's completion receipt.
   * Absence cannot establish completeness for historical model output. */
  textTruncations?: import('./textRetention').TextTruncation[]
  id: string
  parentId: string | null
  /** Extra parents, for synthesis nodes that fuse two branches. */
  bridgeFrom?: string[]
  /**
   * Typed edges beyond the tree, both drawn by a person and both optional.
   *
   * `dependsOn` names the claims this one RESTS ON that are not its parent —
   * the premise on another branch a mechanism quietly assumes. `attacks` names
   * the claims this one argues AGAINST that are not its parent — a
   * contradiction raised under one node about another. The tree carries the
   * one parent edge and `bridgeFrom` the extra parents of a synthesis; these
   * are the two relations a reader kept having to hold in their head instead.
   *
   * READ by `core/standing.ts`, which walks them the way `taint.ts` walks
   * parents — a refutation reaches everything that rests on the refuted claim,
   * and a live refutation of an attacker clears what it attacked — and by
   * nothing that spends: an edge never runs a pass. Ids only, and only ids
   * that exist in this graph, which `persist.ts` re-checks on the way in.
   *
   * User-authored, so adding them bumped `SCHEMA` (v12) for the reason the
   * brief did: an older enumerating codec would strip an edge somebody DREW.
   */
  dependsOn?: string[]
  attacks?: string[]

  /** 2–6 words. What renders on the canvas. */
  label: string
  /** One precise sentence. The actual proposition. */
  claim: string
  /** 2–4 sentences of real reasoning: mechanism, why it follows, what it implies. */
  reasoning: string
  /**
   * This node carries a complete reasoning kernel rather than its final reader-
   * facing explanation. Explicit state, never inferred from string length.
   */
  compact?: true
  /** Detail authored later, without replacing the original model's kernel. */
  elaboration?: NodeElaboration

  kind: NodeKind
  /** 0–1. How much the reasoning supports this. Drives strand thickness. */
  confidence: number
  /** 0–1. How far from the obvious. Drives node glow and breakthrough ranking. */
  novelty: number
  /** 0–1. How much this fights its parent. Bends the strand toward the tension hue. */
  tension: number

  evidence: string[]
  nextSteps: string[]

  /** A path that has been reasoned to exhaustion. Renders as a fading strand. */
  terminal: boolean
  terminalReason?: string
  /**
   * Present — always as `'constraint'` — when the ONLY thing closing this path is
   * a constraint the user set, rather than the argument itself. Absent means
   * reasoned out, which is what every node written before directives existed
   * meant.
   *
   * The distinction is the whole value of asking for it: a branch killed by a
   * theorem is dead, while one killed by the user's own budget bound comes back
   * the moment they relax it — and the sidebar counts these separately so a
   * relaxable dead end is never mistaken for a refuted one.
   */
  terminalKind?: 'constraint'

  depth: number
  createdAt: number
  /** Durable insertion sequence. Optional only for pre-v9/in-memory fixtures. */
  createdOrdinal?: number
  /** Set when the user rewrites this node's understanding. */
  editedAt?: number
  /**
   * Set when a rewrite carried the reader's OWN reasoning as well as their claim
   * (`reviseNode`'s `reasoning`). Absent, `reasoning` on a rewritten node is
   * whatever was there before the rewrite — the model's, written for the words
   * the reader replaced — and `reasoningPredatesRewrite` keeps it out of every
   * pass. Optional and read defensively, so no schema bump.
   */
  reasoningEditedAt?: number

  /* The four epistemic fields. Their order here is the order they appear in
     `makeNode`, in `sanitizeSession`, and in an exported document — one order,
     three files, so a reader can diff them. */

  /** What happened when someone looked outside the graph. Absent on almost
   *  every node, which is the normal state and not a deficiency. */
  verification?: Verification
  /**
   * Earlier source checks, oldest first. The current check remains in
   * `verification` so every existing reader has one canonical latest record;
   * rechecking moves that record here before installing its successor.
   *
   * The split avoids two competing "latest" fields while making the evidence
   * ledger append-only. Absence and an empty array mean the same thing.
   */
  verificationHistory?: Verification[]
  /** Explicit before/after heads created from changed source records. */
  evidenceRebases?: EvidenceRebase[]
  /**
   * What a search returned about the question itself. On the SEED only — the
   * codec and `makeNode` drop it anywhere else — and never read by a gate, a
   * stop condition, the taint walk or the canvas hue: it has no reading.
   */
  survey?: FieldSurvey
  /** Earlier surveys, oldest first, behind the live one — `verificationHistory`'s
   *  shape, for its reason: a re-survey is a second paid search, not an edit. */
  surveyHistory?: FieldSurvey[]
  /** What an adversarial panel found about this node's standing. One per node; a
   *  re-run replaces it. Cleared by a rewrite — a panel that ruled on wording
   *  the user has replaced describes a claim that is no longer there. */
  panel?: PanelCheck
  /** What a model from ANOTHER provider made of this claim. One per node; a
   *  re-run replaces it. NOT cleared by a rewrite, and it follows `verification`
   *  rather than `panel` in that: another company's model really did examine
   *  that text, so the record stands and `auditLive` reads the two timestamps to
   *  decide whether it still describes the claim above it. */
  audit?: Audit
  /** What actually happened. Outranks every other field here, including the
   *  model's own confidence. */
  outcome?: Outcome
  /**
   * When the person expects to know, as epoch milliseconds: the date an
   * experiment reports, a paper appears, a market closes.
   *
   * The resolution queue in `core/resolution.ts` is built from this field and
   * nothing else. A claim with a date is a promise to record an outcome, and
   * the queue is the list of promises, due first. A DATE, not a deadline — it
   * is never clamped to now on the way off disk, because a date in the past is
   * the queue's most useful row, a promise that is overdue. Cleared by
   * recording the outcome it was waiting for. User-authored, under the v12
   * bump, for the reason `dependsOn` gives.
   */
  resolvesBy?: number
  /**
   * The far side of a synthesis that came from another exploration.
   *
   * It cannot live in `bridgeFrom`: the layout seats a bridged node on the
   * bisector of two parents and the renderer draws a strand to each, and a
   * foreign parent has no position on this canvas. Putting it there degrades to
   * silence — indistinguishable from a local partner that was pruned, which is
   * a different fact and already reads differently.
   */
  foreignFrom?: ForeignRef
  /**
   * Present exactly when `kind` is `'conjecture'`: what it departs from, the
   * leap, the prediction that separates the two, and the test that would kill
   * it. A conjecture without this record is a claim with a label, and the codec
   * drops the kind back to `'mechanism'` rather than restore one.
   */
  conjecture?: ConjectureRecord
  /**
   * The conjecture whose "departs from" this node carries, when it was landed
   * from one by the press that puts the expectation on the graph.
   *
   * The TEXT is that conjecture's model's, and `model` names it. The ratings are
   * nobody's: no model was asked how sure, how distant or how opposed the
   * expectation is, so every reader that grades or compares a self-rating skips
   * this node, and the inspector prints no rating for it.
   */
  carriedFrom?: string
  /**
   * Which model reasoned this node.
   *
   * On the node as well as on the op, for the same reason `origin` is: the op
   * ledger is a ledger and is sliced to `MAX_OPS` on the way to disk, while the
   * graph is the artefact. Without it, a long exploration silently stops being
   * able to say which half of a mixed graph came from which model — and that is
   * the one place the mixture is actively misleading, because `confidence` sets
   * strand thickness and `novelty` sets node radius, and two models' self-ratings
   * are not one scale.
   *
   * Absent on the seed (the user's words), on every demo node, and on anything
   * stored before this build.
   */
  model?: ModelStamp
  /** The user has folded this node's descendants away. View state, but it
   *  belongs to the session: it survives a reload and it changes the layout. */
  collapsed?: boolean
  /** When the fold last changed. Drives the chevron the way `birth` drives
   *  growth — a pure function of now − foldAt, with no accumulator. */
  foldAt?: number
  /**
   * How far the user has pulled this node along its own spoke, in world units.
   *
   * The same category of state as `collapsed`, and stored beside it for the
   * same reasons: it is the reader's arrangement rather than the model's
   * reasoning, it survives a reload, it changes the layout, and undo carries it
   * forward instead of rolling it back. Bounded and normalised by
   * `clampStretch` — see layout.ts, which is the only thing that reads it.
   */
  stretch?: number
  /** Which operation produced it — shown in the inspector, used by the engine. */
  origin: NodeOrigin

  /** Layout, filled by `layout.ts`. World-space. */
  x: number
  y: number
  angle: number
  radius: number

  /** Animation. `birth` drives the growth sweep of the incoming strand. */
  birth: number
  /**
   * The node's own pulse stamp: set at creation, by an undo landing on it, and by
   * a pass that rewrites it in place. A running pass touching a node does NOT
   * write here — that rides `core/pulse.ts`, a clock beside `motion`, so a glow
   * never replaces the session (docs/performance-audit.md, finding 5). Read it
   * through `pulses.of(node)`, which takes the later of the two.
   */
  lastPulse: number
}

/** The six graph-growing verbs an autonomous run can combine in one packet. */
export type RunVerb = 'expand' | 'deepen' | 'challenge' | 'diverge' | 'experiment' | 'invert'

/**
 * The logical operations that derive nodes through the ordinary reasoning
 * contract. Checks carry separate prompts and schemas even when a panel or
 * audit may admit a dissenting node.
 */
export type DeriveOp = RunVerb | 'bridge' | 'revise' | 'outcome' | 'rebase' | 'conjecture'

/**
 * One logical Pathways move. A portfolio is deliberately absent: it is a
 * physical pass that owns several separately recorded logical moves.
 */
export type LogicalOpKind =
  | DeriveOp
  /** One search pass: what sources say, and whether anyone said it first. */
  | 'verify'
  /** The same node reasoned again, independently, under other lenses. */
  | 'panel'
  /** A branch handed to a model from the other provider, to be attacked. */
  | 'audit'
  /** Three searches over one candidate pair, and the arithmetic over what came
   *  back. Distinct from `bridge`, which proposes pairs and searches nothing. */
  | 'link'
  /** A structured research workflow whose durable product lives in
   *  `Session.research`, not as a newly-authored graph node. */
  | 'research'
  /** One search of the seed question itself — what the field has found, tried
   *  and disputed. It authors no node and carries no reading; its product is
   *  `ReasonNode.survey` on the seed. See `core/survey.ts`. */
  | 'survey'
  /** One searched pass over one conjecture, assuming it has been said before
   *  and going after where. Authors no node; its record rides on the
   *  conjecture. */
  | 'priorArt'
  /** One searched pass over one node's subject, reporting where the literature
   *  is unsettled. Authors no node; its record rides on the session. */
  | 'sweep'
  /** One pass proposing the move list a programme run would walk. Lands nothing;
   *  the list waits for a person's approval. See `Programme`. */
  | 'plan'
  /** Sharpening a seed before a graph exists. Produces no node. */
  | 'sharpen'

/** One model-pass record. A portfolio pass owns several logical run verbs. */
export type OpKind = LogicalOpKind | 'portfolio'

/**
 * The provenance a node may carry. Checks that never author nodes and the
 * portfolio envelope itself cannot be a node's origin.
 */
export type NodeOrigin =
  | Exclude<
      LogicalOpKind,
      'verify' | 'link' | 'research' | 'plan' | 'sharpen' | 'survey' | 'priorArt' | 'sweep'
    >
  | 'seed'
  | 'manual'

/** One logical angle and the nodes it contributed to a shared physical pass. */
export interface PortfolioPart {
  kind: RunVerb
  targetId: string
  produced: string[]
}

/** The logical work and provenance owned by one physical portfolio pass. */
export interface PortfolioRecord {
  parts: PortfolioPart[]
  /** Present when this same request also detailed its compact target. */
  elaboratedTargetId?: string
  /** Nodes the response supplied after the run's node allowance was spent. */
  droppedForSize?: number
}

export interface Op {
  id: string
  kind: OpKind
  targetIds: string[]
  startedAt: number
  finishedAt?: number
  produced: string[]
  /** Required exactly when `kind` is `portfolio`. */
  portfolio?: PortfolioRecord
  error?: string
  /**
   * What that error WAS, as the closed union rather than as English.
   *
   * `error` is written for a person to read, and `run.ts` used to recover the
   * classification back out of it by regex — so a copy edit to a provider's error
   * string silently disabled the run's stop condition and no test caught it. This
   * is the typed channel that path should have been reading all along. See
   * `endsTheRun`.
   *
   * Named `errorKind` and not `kind` because `kind` on this record is already the
   * verb that ran, and two differently-meaning `kind`s on one object is how a
   * reader ends up matching the wrong one.
   *
   * Present only for a failure the PROVIDER reported. A pass that failed on its
   * own terms — a cross-examination that returned no usable verdicts — records a
   * note and no kind, because that is a failed move rather than a dead account.
   *
   * Deliberately not persisted: `outcomeOf` resolves ops out of the live store and
   * only ever for ops the current run just created, so a stored op can never reach
   * it. Adding it to `persist.ts`'s allowlist would mean a `SCHEMA` touch for data
   * nothing reads back.
   */
  errorKind?: ModelError['kind']
  /**
   * The model declined this line of reasoning.
   *
   * Distinct from `error` and from an empty `produced`, and the distinction is the
   * whole reason this field exists: a refusal used to reach `endOp` with no error
   * and no nodes, which is byte-identical to "no new distinct moves found on that
   * branch" — so anything reading the ledger reported the model's safety layer as
   * a fact about the idea. An autonomous run does exactly that, three times, and
   * would then close the branch.
   *
   * Absent on every stored op and on every pass that was not refused, which is why
   * it needs no migration: absence has always meant "not refused" and still does.
   */
  refused?: boolean
  /** What it said, when it said anything. Printed verbatim rather than
   *  paraphrased — a paraphrase of a refusal is the tool speaking for the model
   *  about why it would not speak. */
  refusalNote?: string
  /**
   * Which safety filter declined it, where the provider named one — Anthropic's
   * `stop_details.category`, an open set ("bio", "cyber", …). What lets the
   * message name the filter and a model that answers (D312). Absent — on every
   * op recorded before it, and on a refusal the provider left uncategorised —
   * the message still says it was the filter, and names no topic and no model.
   */
  refusalCategory?: string
  /**
   * The summarised reasoning trace for this call, kept whole rather than as the
   * 600-character tail the ticker shows.
   *
   * One op, one trace — even when the op produced five nodes, because the trace
   * is the deliberation that chose all five and cannot honestly be split
   * between them.
   */
  thinking?: string
  /** True when the trace hit a cap and the tail was dropped. */
  thinkingClipped?: boolean
  /**
   * For a bridge scan: how many stored explorations it READ besides this one —
   * not how many the control offered.
   *
   * Nothing in `produced` or `targetIds` distinguishes a within-graph scan from
   * an across one, and an exported ledger row that cannot tell them apart is
   * claiming the wrong provenance for every candidate in the list.
   */
  readExplorations?: number
  /**
   * Which model ran this pass.
   *
   * Optional, and the absence is the information: a pre-authored demo op was
   * produced by nobody, and the export's move table prints an em dash for it
   * rather than attributing the demo to a model that never saw it. Written by
   * `beginOp`, gated on the `spends !== false` flag that already exists and is
   * passed only by `demo.ts`.
   */
  model?: ModelStamp
  /**
   * What this one pass drew, summed across its requests.
   *
   * The per-pass row the step rail is built from — `session.spend` is the same
   * arithmetic pooled per model, and per-model rows cannot say which step drew
   * what. Written only through `addSpend(row, opId)`, so the two records are two
   * views of one write and can never disagree by more than a missing attribution.
   * A panel's three seats and a link scan's gathers merge here, which is safe
   * because one op runs on one model by construction.
   *
   * Absent on the demo, on failed passes that billed nothing, and on every op
   * stored before per-pass rows existed — `core/ledger.ts` reports that absence
   * as "unrecorded" rather than free, because the two are opposite facts.
   */
  spend?: Spend
  /**
   * Exact physical model calls retained by future builds.
   *
   * Optional is the migration boundary: historical operations never had this
   * material, so absence must remain an honest omission rather than trigger a
   * reconstruction from today's prompts or settings.
   */
  modelCalls?: ModelCallReceipt[]
  auditCoverage?: AuditCoverage
  narration?: {
    purpose: string
    parentAction: { kind: 'operation' | 'run'; id: string }
    /**
     * The words the reader was shown, carried only where the receipt that holds
     * them is not: a share link leaves every receipt behind (`core/share.ts`),
     * and without this a copy would hold a narration's row and none of its words.
     * Absent on every narration that still has its receipt, which is where the
     * words are read from (`narrationWords`), so the two can never disagree.
     */
    shown?: string
  }
  /** A conjecture pass only: how its response was admitted. Absent on every
   *  other op, and on a conjecture pass that failed before a response arrived. */
  admission?: ConjectureAdmission
}

/* ── autonomous runs ──────────────────────────────────────────────────────
   A run is a loop that spends a budget on moves. Everything below is a count of
   something that happened or a mechanical classification of a stored record.
   There is deliberately no field naming what a run found, no best node, and no
   score for the run itself — a run reports where it looked. */

/**
 * Why a run ended.
 *
 * Stored and never re-derived: a run that spent its budget and a run whose list
 * emptied leave identical graphs and mean opposite things, and this is the only
 * field that tells them apart.
 *
 * Nothing here can be satisfied by the model's own output. There is no reason
 * meaning "it found something", because the loop has no way to know that — and a
 * stop condition the model could reach by rating its own work is the one thing
 * this tool must not ship.
 */
export type RunStop =
  | 'moves'
  | 'tokens'
  /** A user-authored evidence profile's elapsed-time ceiling was reached. */
  | 'clock'
  | 'size'
  /**
   * The estimated spend reached the ceiling the user set.
   *
   * Separate from `tokens` although both are ceilings on the same passes, because
   * they answer different questions and only one of them was chosen by a person:
   * `tokens` is this app's own guard against a run drawing more than its move
   * count implied, and this is a number the user typed in dollars. Collapsing
   * them would report an amount somebody set as an internal limit they had never
   * heard of.
   */
  | 'spend'
  /** The condition the user set was met. The one ending a run reaches by
   *  SUCCEEDING at something — see `UntilCondition` for why the somethings are
   *  facts from the grounding record and never the model's self-ratings. */
  | 'until'
  /** A mechanically evaluated evidence-profile stopping clause was met. */
  | 'protocol'
  /** The list is empty: every open end is closed, folded, past the depth a run
   *  works to, or has had every verb this policy spends. */
  | 'frontier'
  /** A programme run walked to the end of the list the user approved. Its own
   *  ending rather than `frontier`, because the list was finite by construction
   *  and reaching its end says nothing about the graph's open ends. */
  | 'programme'
  /**
   * The candidate a pursue run was confined to stopped, or was dismissed.
   *
   * A FINDING rather than a limit, which is why it is not `frontier`: the run
   * set out to move one candidate up the ladder and the candidate came off it —
   * refuted by the check the run itself made, or found to be published work.
   * Reported apart from `until` because nobody set it as a condition.
   */
  | 'pursuit'
  /** Three model passes in a row came back with no nodes, no error and no refusal
   *  — passes, not logical moves; a portfolio pass's empty angles are one. Records
   *  from before that rule may have stopped on fewer passes, which is why the head
   *  below carries no number and the account prints the record's own. */
  | 'stalled'
  /** The model declined three moves. A fact about the passes, never about the idea. */
  | 'declined'
  | 'stopped'
  | 'unreachable'
  | 'replaced'

/**
 * The head line per ending, in one map for the same reason `OP_LABEL` is one map:
 * the pod, the account and the export all print it, and three copies of a sentence
 * is how two surfaces come to describe one run two ways.
 *
 * No numbers in any of them. The counts sit underneath, where they can be read
 * against each other — a head line carrying its own arithmetic drifts from the
 * block below it the first time either changes.
 */
export const RUN_STOP_HEAD: Record<RunStop, string> = {
  moves: 'The move budget ran out.',
  tokens: 'The token ceiling was reached.',
  // "Would have crossed", not "was reached": the loop stops before the pass that
  // could cross the ceiling rather than after the one that did, so a head line
  // saying it was reached would claim the run spent an amount it deliberately
  // stopped short of. The figures underneath show the gap.
  spend: 'The next pass would have crossed your spending ceiling.',
  until: 'It reached the condition you set.',
  protocol: 'The evidence protocol stopping rule was met.',
  clock: 'The evidence protocol’s elapsed-time ceiling was reached.',
  size: 'The graph reached the size a run stops at.',
  frontier: 'Nothing left on this run’s list.',
  programme: 'It reached the end of the list you approved.',
  pursuit: 'The candidate it was pursuing stopped.',
  stalled: 'The model stopped returning anything new.',
  declined: 'The model declined three moves.',
  stopped: 'You stopped it.',
  unreachable: 'The model stopped answering.',
  replaced: 'The exploration changed while it was running.',
}

/**
 * The key that actually decided a move.
 *
 * A closed set and not free prose, because this string is the audit: it is printed
 * live, stored, and exported, and it has to BE the reason rather than a
 * description of one. The policy emits whichever key first separated the winner
 * from the runner-up, so a reader can re-run the comparison by hand.
 */
export type MoveReason =
  /** The recorded agenda's next eligible step; the move retains its full rule. */
  | 'agenda'
  /** The node the run is already working, with angles left to try on it. */
  | 'in-progress'
  | 'least-worked'
  | 'smallest-branch'
  | 'contradiction'
  | 'gap'
  | 'disputed'
  | 'stale'
  /** Something this node was reasoned from has since been refuted, and the
   *  reasoning under it has not been re-derived. Distinct from `stale`, which is
   *  the weaker fact that a result of any bearing landed above it. */
  | 'foundation-broke'
  | 'oldest'
  | 'only-one'
  /**
   * The claim more of the graph rests on than any other the predicate admitted.
   *
   * Shared by the source check and the panel, which rank their candidates the same
   * way — by the size of the subtree beneath them — over two different candidate
   * sets. Its label therefore says "anything else this pass could take" rather than
   * naming one pass's predicate: a second wording for one arithmetic is how two
   * surfaces come to describe the same comparison two ways.
   */
  | 'load-bearing'
  /** A cross-examination: the branch carrying more claims no other model has
   *  examined than any other. Distinct from `load-bearing`, and deliberately: that
   *  one measures what rests on a node, this one measures what is left to cover, and
   *  an audit that re-attacked the branch it just finished would rank top on the
   *  first and bottom on the second. */
  | 'unexamined'
  /** A pursue run's first move: the synthesis itself, grounded before anything
   *  under it is spent on. */
  | 'synthesis-first'
  /** A pursue run: the least-worked open end inside the two cones the candidate
   *  was fused from. The same arithmetic as `least-worked`, over a smaller list,
   *  and named apart so the account says which list. */
  | 'cone'
  /** A programme run: the next step on the list the user approved as one batch. */
  | 'programme'
  /** The closing scan, which targets the whole graph and no node. */
  | 'whole-graph'
  /**
   * A source check chosen among candidates nothing structural separated, by the
   * judge's probability that a search could settle the claim. The LAST key
   * before arrival order and only there: what rests on a claim and how open it
   * is still decide first, and the judge never removes a candidate (D106).
   */
  | 'checkable'
  /**
   * A conjecture pass: more records of what does not fit sit beneath this node —
   * none with a conjecture under it yet — than beneath any other. Counted by
   * PARENTAGE and never by the anchors a conjecture names, which are model
   * output. See `conjectureCandidate`.
   */
  | 'unexplained'
  /** A prior-art search on a conjecture this run landed, taken in the order the
   *  conjectures landed. */
  | 'landed-unsearched'

/**
 * No adjectives, and none of these says "promising" or "interesting" — the policy
 * does not compute those, and printing a word the comparison did not earn is how a
 * readout like this starts lying.
 */
export const MOVE_REASON: Record<MoveReason, string> = {
  agenda: 'the next eligible step in the recorded agenda',
  'in-progress': 'it is the end already being worked, and there are angles left on it',
  'least-worked': 'the least-worked open end',
  'smallest-branch': 'the least-worked open end on the smallest branch',
  contradiction: 'the least-worked open end, and it is a contradiction',
  gap: 'the least-worked open end, and it is a knowledge gap',
  disputed: 'the least-worked open end, and a source disputes it',
  stale: 'it was reasoned before a newer outside record above it',
  'foundation-broke': 'it was reasoned from something that has since been refuted',
  oldest: 'nothing separated the candidates, so the oldest went first',
  'only-one': 'the only open end left that this policy will spend on',
  'load-bearing': 'more of the graph rests on it than on anything else this pass could take',
  unexamined: 'more claims here have never been examined by another model than anywhere else',
  'synthesis-first': 'the synthesis itself, grounded before anything under it is spent on',
  cone: 'the least-worked open end inside the two branches this candidate was fused from',
  programme: 'the next step on the list you approved',
  unexplained:
    'more records of what does not fit sit beneath it, with no insight under them yet, than beneath anything else',
  'landed-unsearched':
    'an insight this run proposed, searched for prior art in the order the insights landed',
  'whole-graph': 'the whole graph, once, at the end',
  checkable:
    'nothing structural separated the candidates, and the judge put the higher probability on a search settling this one',
}

/**
 * A stopping condition the USER set, counted over the grounding record.
 *
 * Five kinds, and each is a fact a run collects that does not come from the
 * reasoning model's opinion of its own work: a source-check reading rests on
 * sources validated against what the search actually returned, a split is a
 * mechanical count over three blind seats, an objection was raised by a model
 * from another company, and a rung is `bridgeStanding` in `core/rung.ts`, which
 * reads those same records and never a candidate's own score. `novelty`,
 * `confidence` and `tension` are deliberately
 * not expressible here — a run that stopped on the model's enthusiasm would be
 * the exact failure `runPolicy`'s header forbids, reached through the front door.
 */
export interface UntilCondition {
  kind: 'refuted' | 'supported' | 'split' | 'objection' | 'rung'
  /** How many, 1 or more. The run stops the moment the count reaches it. */
  n: number
  /** For `rung` only: the rung a candidate has to stand on to count. Absent on
   *  every other kind, and read as `realised` if a stored `rung` lost it. */
  rung?: BridgeRung
}

/** One wording per condition, shared by the chip, the run control, the account
 *  and the export — the `RUN_STOP_HEAD` rule: three copies of a sentence is how
 *  two surfaces come to describe one run two ways. */
export function untilLabel(u: UntilCondition): string {
  const n = u.n
  switch (u.kind) {
    case 'refuted':
      return `${n} claim${n === 1 ? '' : 's'} refuted by sources`
    case 'supported':
      return `${n} claim${n === 1 ? '' : 's'} supported by sources`
    case 'split':
      return n === 1 ? 'one split panel' : `${n} split panels`
    case 'objection':
      return `${n} objection${n === 1 ? '' : 's'} from another provider`
    case 'rung':
      return `${n} synthesis candidate${n === 1 ? '' : 's'} earning the ${u.rung ?? 'realised'} rung`
  }
}

/**
 * A list of moves a model proposed and a person approved as ONE batch.
 *
 * The model writes the list; the person reads it and presses once; the policy
 * then walks it in order and does no choosing of its own. Between the press and
 * the end of the list nothing reads a self-rating — `runPolicy`'s header holds —
 * and the run stops when the list is exhausted (`programme`) or when a stop it
 * shares with every other run fires first. A step names a node that may be gone
 * by the time it is reached; the loop skips it and records the skip as the
 * move's outcome rather than picking a substitute, because a substitute is a
 * choice nobody approved.
 */
export interface ProgrammeStep {
  nodeId: string
  verb: RunVerb | 'verify' | 'panel' | 'audit'
  /** The model's one sentence on why this step. Kept because the approval was
   *  of the sentence as much as of the verb. */
  why: string
}

export interface Programme {
  steps: ProgrammeStep[]
  /** Which model proposed the list. Required for the reason `Run.model` is. */
  model: ModelStamp
  proposedAt: number
}

/** The ceilings as they stood when the run started, so a reader sees what it was
 *  allowed to do and not only what it did. */
export interface RunBudget {
  moves: number
  outputTokens: number
  /**
   * The wall-clock allowance, in milliseconds.
   *
   * The whole-run clock was removed, for two reasons that compound: it was tested
   * only between passes, so it could not catch the hung request it looked like it
   * bounded — and against a slow-but-honest run it fired only after the time it
   * guarded was already spent, truncating the run without refunding a second.
   * A hung pass is bounded by the transport's per-pass deadline instead
   * (`PASS_TIMEOUT_MS`), which also bounds total wall time at `moves` times the
   * deadline without any per-run dial.
   *
   * Optional because ordinary runs still use per-pass deadlines alone. A new run
   * carries this only when its durable evidence profile declares a whole-run
   * elapsed ceiling. It is checked between passes and therefore bounds whether
   * another pass may start; the transport deadline remains the hung-pass bound.
   */
  ms?: number
  /**
   * The most nodes this run may add — its own contribution, never the graph's
   * size, which has no ceiling of its own.
   *
   * The same field was the size the GRAPH stopped at until D74, and a run stored
   * from then that ended `size` carries a `produced` well under it. `RunReport`
   * tells the two apart by that count rather than by any schema mark, because the
   * record already says which happened.
   */
  nodes: number
  checks: number
  /**
   * A ceiling in dollars, or absent when the user set none.
   *
   * Optional, and the absence means no ceiling rather than a ceiling of zero —
   * which is also what makes it storable without a schema bump: a run recorded
   * before this field existed genuinely had no spending ceiling, so reading it
   * back as absent is not a default standing in for a fact, it IS the fact.
   *
   * Stored on the budget beside the other four rather than derived, because it is
   * the only one of the five the user chose and the account has to be able to show
   * what they chose — including on a run that never came near it.
   */
  usd?: number
  /**
   * Panel passes this run was allowed, or absent when it was allowed none.
   *
   * Optional for exactly the reason `usd` is, and the same sentence applies: a run
   * recorded before this field existed genuinely ran no panels, so reading it back
   * as absent is not a default standing in for a fact, it IS the fact.
   *
   * Counted in PASSES OF THE VERB and not in requests, which is worth stating
   * because this one verb is three requests. One panel is one entry here and three
   * passes on the ledger, so the projection charges it three times — see
   * `projectRunUsd`, which would otherwise under-quote a panel run by two thirds.
   */
  panels?: number
  /**
   * Cross-examination passes this run was allowed, or absent when it was allowed
   * none. Optional on the same grounds as the two above.
   *
   * An audit is the one pass a run makes on a DIFFERENT provider, which is why it
   * is a count rather than a flag: each one hands a whole branch of up to
   * `AUDIT_MAX_CLAIMS` claims over at once, so two audits mean two branches and
   * never the same branch twice.
   */
  audits?: number
  /**
   * Conjecture passes this run was allowed, or absent when it was allowed none —
   * which is the default, and the only default until the live evaluation for
   * this arm has been run (D281). Optional on the same grounds as the three
   * above.
   *
   * Scheduled like the audit, behind schedule over this count and never before
   * `CONJECTURE_FLOOR` produced nodes. A run given any also spends its CHECK
   * allowance on a prior-art search for each conjecture it lands, oldest first,
   * before any source check — so this field is also what tells the projection and
   * the reserve that a check slot may be the dearer search.
   */
  conjectures?: number
  /**
   * Stop the moment this condition is met, or absent when none was set.
   *
   * On the budget although it is not a ceiling, because it shares the ceilings'
   * one property that matters to the account: it was chosen before the run and
   * the report has to say what was chosen — a run that ended `until` with the
   * condition itself lost would be an ending nobody could interpret. Optional on
   * the same storability grounds as `usd`.
   *
   * It can only stop a run SOONER. Every ceiling above still binds, so a
   * condition never reached changes nothing about what a run may spend.
   */
  until?: UntilCondition
}

/**
 * How many of a run's nodes reached each ending in the model's reasoning — the
 * three `reasonedEndingOf` names (`core/endings.ts`), counted as the nodes land.
 * Written out here rather than imported so this file keeps importing nothing
 * from the modules that import it.
 */
export type ReasonedEndingCounts = Partial<
  Record<'objection-stood' | 'path-failed' | 'open', number>
>

/**
 * What was still open when it stopped, broken out by why it was out of reach.
 *
 * A single number would under-report exactly where it matters: a run that hits the
 * depth ceiling leaves a whole ring untouched, and folding that into "reachable"
 * makes the one figure engineered to prevent an impression of completeness the
 * figure that creates it.
 */
export interface RunOpen {
  /** Open ends this policy could still have taken. */
  reachable: number
  /** Open ends past the depth a run works to. */
  pastDepth: number
  /** Open ends inside branches the user folded away. */
  folded: number
  /** Nodes the model marked terminal with no result recorded against them. */
  modelClosed: number
}

export interface RunMove {
  at: number
  kind: LogicalOpKind
  /** Empty for the closing scan, which has no target. */
  nodeId: string
  /** A copy. The node may be pruned or rewritten long before anyone reads this,
   *  and a move row that cannot say what it was about is not a record. */
  label: string
  why: MoveReason
  /** The exact queued intent and selection rule, retained with the billed move. */
  agenda?: { stepId: string; attemptId: string; rule: string }
  /** The join to the ledger, and through it to the full reasoning trace. This is
   *  why `Op` needs no run field: a field written inside `beginOp` would tag the
   *  user's own keypresses during the gaps between passes. */
  opId: string
  /**
   * Five outcomes, because a list of produced ids cannot tell them apart.
   * `refused` is separate from `nothing` on purpose — collapsing them reports a
   * safety refusal as an exhausted branch, and that is a claim about the idea.
   * `incomplete` means the shared packet ended before this logical angle arrived;
   * it is the opposite of a valid empty result.
   */
  outcome: 'nodes' | 'nothing' | 'refused' | 'error' | 'incomplete'
  produced: number
  /**
   * Actual provider requests owned by this logical row when it can differ from
   * one. Present on new panel rows because auth or Stop can end the three-seat
   * panel after its first or second request.
   */
  modelPasses?: number
  /** The error text or the refusal note, verbatim. */
  note?: string
  /**
   * On a source-check move a judge was asked about: the probability it put on a
   * search settling this claim, and which judge said so. Printed beside the
   * move's reason so a reader can see the number that ordered a tie — and see it
   * whether or not it did, since a run that consulted a judge owes the reader
   * every answer it paid for, not only the ones that decided something.
   */
  judge?: { checkability: number; model: ModelStamp }
}

/**
 * What one move actually did, in a few words.
 *
 * In ONE map for the reason `RUN_STOP_HEAD` is one map: the account and the exported
 * document both print this, and two copies of the sentence is how two surfaces come
 * to describe one run two ways. It already was two copies, and they already agreed
 * only by luck.
 *
 * The three grounding passes need their own arms because `produced` does not mean the
 * same thing for them. A source check adds no nodes BY DESIGN; a panel rules on a
 * claim and may land a dissent node; an audit's nodes are proposed criticisms.
 * Completed reviews with no specific criticism are distinct from incomplete work.
 */
export function moveResult(m: RunMove): string {
  // An empty conjecture pass is an answer — nothing the model could propose held
  // up to its own contract — and says so in its own words rather than a branch's:
  // the status table's, which the pass's toast and the voice's digest read too.
  if (m.kind === 'conjecture' && m.outcome === 'nothing')
    return STATUS_WORD.noConjectureLanded.toLowerCase()
  // The words are the status table's, lower case because every surface sets
  // them after a dash; "did not run" is also what the thread says of a pass
  // with no row, and one table is how the two stay one phrase.
  if (m.outcome === 'nothing') return STATUS_WORD.nothingNew.toLowerCase()
  if (m.outcome === 'refused') return STATUS_WORD.declined.toLowerCase()
  if (m.outcome === 'error') return STATUS_WORD.didNotRun.toLowerCase()
  if (m.outcome === 'incomplete') return STATUS_WORD.incomplete.toLowerCase()
  const n = m.produced
  const nodes = `${n} node${n === 1 ? '' : 's'}`
  if (m.kind === 'verify') return OP_PAST.verify.toLowerCase()
  // The reading lives on the conjecture, dated; this row says only that it ran.
  if (m.kind === 'priorArt') return OP_PAST.priorArt.toLowerCase()
  if (m.kind === 'conjecture') return conjecturesProposed(n)
  if (m.kind === 'panel') {
    const passes = m.modelPasses ?? 3
    const result = `panel recorded after ${passes} of 3 lens request${passes === 1 ? '' : 's'}`
    return n > 0 ? `${result}, one dissent landed` : result
  }
  if (m.kind === 'audit') {
    return n > 0
      ? `${n} proposed criticism${n === 1 ? '' : 's'} recorded`
      : STATUS_WORD.reviewedNoObjection.toLowerCase()
  }
  return nodes
}

/**
 * Physical model passes represented by logical run rows.
 *
 * Shared portfolio rows count once by `opId`; a panel counts three because its
 * one operation deliberately contains three blind model seats. Everything else
 * is one request-shaped pass. This is reporting arithmetic only and never a stop
 * condition.
 */
export function modelPassesFor(moves: readonly RunMove[]): number {
  const seen = new Set<string>()
  let passes = 0
  for (const move of moves) {
    if (seen.has(move.opId)) continue
    seen.add(move.opId)
    passes += move.kind === 'panel' ? (move.modelPasses ?? 3) : 1
  }
  return passes
}

/**
 * How far a run got, in the ONE format the thread's run row, the run pod and the
 * account all print: "13 of 24 moves · 8 passes".
 *
 * Two units, each under its own word. The budget is denominated in logical moves
 * (D47, D74), and a portfolio pass works two or three of them, so "of 24" is only
 * ever a count of moves and the passes ride beside it named as passes. The thread
 * row once counted passes and called them moves — "8 of 12 moves" over an account
 * saying "12 logical moves across 8 model passes", and "13 of 24 moves" beside a
 * narration correctly saying the whole budget had been spent. Three surfaces
 * wording one fact three ways is how that happened, so there is one sentence.
 *
 * Numbers in, not a run: the pod reads the live projection's counts, the other
 * two the stored record's, and `modelPassesFor` is what both of those are.
 */
export function runProgress(moves: number, budget: number, passes: number): string {
  return `${moves} of ${budget} move${budget === 1 ? '' : 's'} · ${passes} pass${passes === 1 ? '' : 'es'}`
}

/**
 * Model passes whose every logical row came back with nothing new.
 *
 * The count a stalled run's account states. It used to say "a fact about three
 * passes" whatever the record held, and the run it was first read on had made
 * ONE: three empty angles in a single portfolio response, which the stall streak
 * then counted per angle. The streak counts passes now, but the account reads
 * the record rather than the rule, so a run stored before that change is told
 * what it actually did. Reporting arithmetic only, like its sibling above.
 */
export function emptyPassesFor(moves: readonly RunMove[]): number {
  const byOp = new Map<string, boolean>()
  for (const move of moves) {
    byOp.set(move.opId, (byOp.get(move.opId) ?? true) && move.outcome === 'nothing')
  }
  let empty = 0
  for (const all of byOp.values()) if (all) empty++
  return empty
}

/**
 * The passes a run counted toward its failure limit, as op ids in the order they
 * failed.
 *
 * Two shapes, the second of which carries no `error` row. A request that failed
 * has one. A portfolio pass that ANSWERED, but with a packet that was malformed or
 * incomplete and admitted no node, is a failure too — `portfolioPassTally` in
 * run.ts — and its rows read `incomplete`, because that is what each angle was.
 * An account ending `unreachable` states this count, so it has to see both: read
 * off `error` rows alone it would say "three passes failed" of a run the loop
 * stopped on four.
 *
 * One shape stays invisible and is named rather than guessed at: a packet whose
 * angles all arrived complete and empty but whose response was cut short. The
 * loop saw the truncation; the record holds only its three `nothing` rows.
 */
export function failedPassesFor(moves: readonly RunMove[]): string[] {
  const byOp = new Map<string, RunMove[]>()
  for (const move of moves) byOp.set(move.opId, [...(byOp.get(move.opId) ?? []), move])
  const failed: string[] = []
  for (const [opId, rows] of byOp) {
    const errored = rows.some((m) => m.outcome === 'error')
    const broken =
      rows.length > 1 &&
      rows.some((m) => m.outcome === 'incomplete') &&
      !rows.some((m) => m.outcome === 'refused' || m.produced > 0)
    if (errored || broken) failed.push(opId)
  }
  return failed
}

/**
 * How many of the RETAINED ledger's passes came from runs.
 *
 * The sibling of `modelPassesFor`, in the other unit, for the one sentence of
 * the form "N of the M passes came from runs" — which must hold both numbers
 * in ONE frame. Two constraints shape it:
 *
 *  - The unit is distinct ops, a panel counting once, because the M is
 *    `session.ops.length` and an N that counted the panel's three seats could
 *    exceed its own total. The physical count cannot be used for M either — an
 *    op does not record how many of a panel's seats actually ran; only the
 *    run's move does.
 *  - The count is INTERSECTED with the ops the session still holds, because
 *    an older document may already have lost operation receipts, and malformed
 *    receipts still fail validation. New saves retain operations named by run
 *    moves, but cannot recreate an absent one. An unintersected count could
 *    therefore exceed the total it is printed against. The intersection makes
 *    the sentence true by construction: a share of the passes THIS document
 *    accounts for.
 */
export function runPassShare(s: Session): number {
  const retained = new Set<string>()
  for (const op of s.ops) retained.add(op.id)
  const counted = new Set<string>()
  for (const run of s.runs) {
    for (const move of run.moves) {
      if (retained.has(move.opId)) counted.add(move.opId)
    }
  }
  return counted.size
}

/**
 * The account's sentence for checks a ceiling could not hold back, in one place
 * for `RUN_STOP_HEAD`'s reason: the Run tab, the export and the closing toast all
 * say it. It names the threshold and what the run did instead, because "skipped"
 * alone reads as a fault — and the whole point is that it was the ceiling doing
 * its job one pass kind at a time.
 */
export function checksSkippedLine(s: NonNullable<Run['checksSkipped']>): string {
  return `${s.count} source check${s.count === 1 ? '' : 's'} skipped: under ${fmtUsd(s.usd)} left for a source check, so the run kept reasoning instead.`
}

/** The same sentence for checks that had nothing to read, said in the same three
 *  places and shaped the same way: the count, the reason, and what the run did. */
export function checksUnreadLine(s: NonNullable<Run['checksUnread']>): string {
  return `${s.count} source check${s.count === 1 ? '' : 's'} skipped: ${s.model} has no web search here, so the run kept reasoning instead.`
}

/** Panel-only request accounting for the stored run report. */
export function panelRunTally(moves: readonly RunMove[]): {
  moves: number
  passes: number
  partial: number
} {
  const panels = moves.filter((move) => move.kind === 'panel')
  return {
    moves: panels.length,
    passes: panels.reduce((sum, move) => sum + (move.modelPasses ?? 3), 0),
    partial: panels.filter((move) => (move.modelPasses ?? 3) < 3).length,
  }
}

/**
 * Whether the node's `reasoning` was written for words the reader has since
 * replaced.
 *
 * A rewrite replaces the claim and re-derives BELOW it; nothing re-reasons the
 * node itself, so the argument left on it argues for a claim that is no longer
 * there. It went on reaching the next check as "ITS STATED REASONING", which
 * handed a checking model an argument for different words as the argument for
 * these. `editedAt` decides, as it decides every other "before your rewrite":
 * the reasoning predates the rewrite unless the rewrite itself carried the
 * reader's own (`reasoningEditedAt`). Read-time, so every document written
 * before this rule — whose rewritten nodes all carry the model's old reasoning,
 * because no surface has ever supplied one — reads the same way with no
 * migration.
 */
export const reasoningPredatesRewrite = (
  n: Pick<ReasonNode, 'editedAt' | 'reasoningEditedAt' | 'reasoning'>,
): boolean =>
  n.editedAt !== undefined &&
  n.reasoning.trim() !== '' &&
  !(n.reasoningEditedAt !== undefined && n.reasoningEditedAt >= n.editedAt)

/** The authored kernel, when it was written for the words on the node now —
 *  otherwise nothing. Every reader that hands reasoning to a pass or matches on
 *  it reads this or `reasoningOf`, never the field. */
export const reasoningKernelOf = (n: ReasonNode): string =>
  reasoningPredatesRewrite(n) ? '' : n.reasoning

/** The complete reader-facing reasoning without erasing the authored kernel —
 *  for the words on the node now, so empty on a node rewritten without new
 *  reasoning (`reasoningPredatesRewrite`). A rewrite clears the elaboration. */
export const reasoningOf = (n: ReasonNode): string => {
  const kernel = reasoningKernelOf(n)
  if (!n.elaboration) return kernel
  return kernel ? `${kernel}\n\n${n.elaboration.reasoning}` : n.elaboration.reasoning
}

/**
 * The reasoning written for the node's previous wording, and whose it was — or
 * null. For the reader, folded on the card and labelled in the export, and for
 * no pass: it is what `reasoningOf` leaves out. `you` only where an earlier
 * rewrite carried the reader's own reasoning and a later one did not.
 */
export function previousReasoningOf(n: ReasonNode): { text: string; by: 'model' | 'you' } | null {
  if (!reasoningPredatesRewrite(n)) return null
  return { text: n.reasoning, by: n.reasoningEditedAt !== undefined ? 'you' : 'model' }
}

/** The actionable steps belonging to the most complete authored representation. */
export const nextStepsOf = (n: ReasonNode): string[] => n.elaboration?.nextSteps ?? n.nextSteps

/**
 * Whether the claim is the label said again — so a card that sets the label as
 * its title and the claim under it would print one thing twice.
 *
 * True of every seed by construction: `startSession` writes the typed words
 * into BOTH fields, because a seed has no model behind it to name it and its
 * own words are the only honest label. The demo's root is built the same way,
 * and a rewrite lands here too when somebody types one sentence into the label
 * box and the claim box alike. Every card that prints the pair asks this before
 * printing the second: the paragraph earns its place by saying something the
 * title did not, and for these nodes it says nothing. One rule for every such
 * card rather than a check per surface, so two cards cannot disagree about one
 * node. An equality after a trim and nothing looser — the two writers of these
 * fields (`startSession`, `reviseNode`) trim on the way in, and a prefix match
 * would start hiding claims that genuinely continue past their label.
 */
export const claimRestatesLabel = (n: ReasonNode): boolean => n.claim.trim() === n.label.trim()

export interface Run {
  id: string
  startedAt: number
  /** Absent when the tab died mid-run, and the absence is the information — the
   *  same rule as `Op.finishedAt`. */
  finishedAt?: number
  /** Absent for the same reason: a run with no ending never reached one. */
  stop?: RunStop
  budget: RunBudget
  /** Opt-in queue snapshot. Later queued steps belong to a later explicit run. */
  agenda?: { stepIds: string[] }
  moves: RunMove[]
  /** Nodes it threaded. Counted from its own ops as they land and never
   *  recomputed from the graph: a prune afterwards must not change what the run
   *  says it did. */
  produced: number
  /** Of those, the ones the model marked terminal — every ending it reasoned to.
   *
   *  Stored for the same reason and worth storing at all because it is the count
   *  a run is most worth reading for: a well-argued dead end tells you where not
   *  to spend the next month, and it is the one thing here that gets more useful
   *  the larger it is. Attributed to the model in every surface that prints it —
   *  the run did not close those paths, it paid for the passes that did. Never
   *  printed as "dead ends": it counts the questions reasoning cannot settle too,
   *  which are the opposite of one (`endings`). */
  dead: number
  /** `dead`, counted apart by the ending each node reached — an objection that
   *  stood, a path that failed, a question reasoning cannot settle — so the
   *  account says them in the endings' own words (`runEndingsLine`). Stored for
   *  `dead`'s reason. Absent on a run stored before they were counted apart,
   *  which reads as `dead` alone and is never split by a guess; no SCHEMA bump. */
  endings?: ReasonedEndingCounts
  /** Source-check passes it ran, and what each reading came back as. Stored rather
   *  than read off the nodes, so a pruned or re-checked node cannot rewrite the
   *  run's account — and so the account can never print "N verified" over readings
   *  that checked nothing. */
  checkReadings: SourceReading[]
  /**
   * Cross-examination verdicts it collected, one per claim examined, or absent when
   * it ran no audit.
   *
   * Stored rather than counted off the nodes for the reason `checkReadings` states,
   * and the reason binds harder here: pruning a proposed criticism afterwards
   * must not rewrite the recorded review. Scope lives on the target's review;
   * the run retains the admitted verdict for its completion accounting.
   *
   * Invalid and truncated responses have no verdict here. Missing context has
   * its own verdict and remains unfinished research. See `runAudit`.
   */
  auditVerdicts?: Audit['verdict'][]
  /** Candidate pairs the closing scan added, or absent if no scan ran. */
  scanned?: number
  /**
   * Scheduled source checks the run passed because too little of its ceiling was
   * left to hold one back, and the reserve a check was held to — absent when it
   * skipped none, which is every run with no ceiling and every run recorded
   * before a check had a reserve of its own. Absence needs no schema bump, for
   * `usd`'s reason: those runs genuinely skipped nothing on this rule.
   *
   * Stored rather than derived, because the figure is the reserve AT THE TIME:
   * list prices move, and an account recomputed from today's would name a
   * threshold the run was never held to. See `ceilingFloors` in `engine/run.ts`.
   */
  checksSkipped?: { count: number; usd: number }
  /**
   * Scheduled source checks the run passed because its check route had nothing to
   * read — no search on the model, and “Prefer my sources” off — and that model,
   * named as the route had it when the checks went off. Absent when it skipped none
   * on this rule, which every run recorded before the rule is: those runs hung at
   * the first such check rather than skipping it, so no schema bump (D293). A run
   * carries this or `checksSkipped`, never both: whichever turned its checks off
   * first is the reason, and the other never fires.
   */
  checksUnread?: { count: number; model: string }
  /** Branches the user closed with "Not there" while it ran. */
  declined: number
  open: RunOpen
  /** The ledger at the moment it started, so the run's own draw is a subtraction
   *  and never a second tally that could disagree with the sidebar. */
  spendBefore: { inputTokens: number; outputTokens: number; searches: number }
  /**
   * What it drew, in dollars, as this app's own upward estimate at list prices.
   *
   * The subtraction `spendBefore` was stored for, performed once at the close and
   * kept — because it cannot be performed later. Every op after the run adds to the
   * same ledger, so an account that recomputed this would print a figure that grows
   * every time the user presses Deepen by hand.
   *
   * NOT A BILL, and no surface may call it one. Absent when the run's model had no
   * published price and on every run recorded before this field existed; in both
   * cases the account says the estimate is unavailable rather than printing zero.
   */
  estimatedUsd?: number
  /**
   * The judge this run consulted before its source checks, and how it went.
   *
   * PRESENT only when a judge was configured at the start: absent means the
   * run ordered its checks by structure and arrival alone, which is the
   * ordinary state and is said in those words by every surface that prints
   * this. `asked` counts claims the judge rated; `failed` counts the calls
   * that did not answer, each of which left its claims ordered by arrival.
   */
  judge?: { model: ModelStamp; asked: number; failed: number; note?: string }
  /** Exact durable evidence-policy clauses applied when this run started. */
  evidencePolicy?: {
    /** Full rules for new runs; absent in older records, never filled from today's profile. */
    profile?: ResearchState['evidenceProfile']
    profileId: string
    profileName: string
    profileVersion: number
    maxMoves: number | null
    maxEstimatedUsd: number | null
    maxElapsedMinutes: number | null
    minSupported: number | null
    minRefuted: number | null
    stopOnDecisionCriterion: boolean
  }
  /**
   * The candidate synthesis a pursue run was confined to, by bridge id, or
   * absent on every other run. The policy reads it to build the two cones; the
   * account prints it so a reader knows why the run touched only one corner.
   */
  pursues?: string
  /** The list a programme run walked, as approved. Absent on every other run. */
  programme?: Programme
  /** Required, alone with `Verification`'s, and for the same reason: a run that
   *  ran on no model did not run. */
  model: ModelStamp
}

/**
 * One side of a candidate synthesis.
 *
 * A local end is an id and nothing else — the graph is right there, and a cached
 * label goes stale the moment the node is rewritten. A foreign end is the exact
 * opposite case: the exploration it names may be gone by the time anyone reads
 * the candidate, so it carries its own copy.
 */
export type BridgeEnd = { where: 'local'; nodeId: string } | { where: 'foreign'; ref: ForeignRef }

/** A page the conjunction search returned that addresses both sides. */
export interface JoiningSource {
  url: string
  /** From the API response, NEVER from the model — same rule as `WebSource`. */
  title: string
  /** `fetch` means the page was opened. A title match and a page saying so are
   *  not the same evidence and must not read as one. */
  via: 'search' | 'fetch'
}

/**
 * The same non-interaction test, run over a scholarly index instead of the web.
 *
 * A SECOND look, stored beside the first and never merged into it. The web
 * gather asks a model's search tool; this asks OpenAlex through the corpus
 * door, with no model in the loop, so an `already-made` here is a paper the
 * index holds rather than a page the model chose to open. The two agree often
 * and disagree usefully — an index that has the pair joined while the web
 * gather missed it is prior art the ladder must not climb past, which is why
 * `bridgeStanding` in `core/rung.ts` reads both. Counts only, like its parent:
 * the reading is arithmetic over what came back, from `core/link.ts`.
 */
export interface IndexLinkage {
  reading: LinkReading
  intermediates: { term: string; aDocs: number; cDocs: number }[]
  joinedBy: JoiningSource[]
  queries: string[]
  aDocs: number
  cDocs: number
  /** Which index. One today; the field is what stops a second from arriving as
   *  a second copy of this record. */
  source: 'openalex'
  scannedAt: number
}

/**
 * What a link scan found out about a candidate, from outside the model.
 *
 * The one record in this app whose headline is a COUNT rather than a reading of
 * prose — see `core/link.ts` for the arithmetic and for why the null has to be
 * earned by a search that went looking for the opposite. Absent on every
 * candidate that has not been scanned, which is the normal state.
 */
export interface StoredLinkage {
  reading: LinkReading
  /** Terms both literatures carry, best first. The material a mechanism is built
   *  from, and empty on every reading except `unjoined`. */
  intermediates: { term: string; aDocs: number; cDocs: number }[]
  /** The mechanism the reasoning pass proposed, and which intermediate carries
   *  it. Absent when the pass found none, or was never run because the reading
   *  did not earn one. */
  mechanism?: string
  via?: string
  /** Pages that already join the two sides. The evidence for `already-made`, and
   *  empty otherwise. */
  joinedBy: JoiningSource[]
  /** Every query the three gathers issued, read off the API response. This is
   *  what makes `unjoined` auditable: an absence only means something next to
   *  what was looked for. */
  queries: string[]
  aDocs: number
  cDocs: number
  /** Whether any page's own text was available. A `titles` scan is weaker and
   *  every surface that prints one says so. */
  depth: 'text' | 'titles'
  /** One line per search step that errored. Present only on `unsound`, which is
   *  the reading they cause. */
  failures?: string[]
  /** The index scan's own reading, when one ran. See `IndexLinkage`. */
  index?: IndexLinkage
  scannedAt: number
  /** Which model searched. Required for the same reason `Verification.model` is:
   *  a check with no model behind it is not a check. */
  model: ModelStamp
}

/**
 * Why a pair was put in front of the model at all — the arithmetic that chose
 * it, kept so the card can say so.
 *
 * `distance` is the number of tree edges between the two ends, through their
 * lowest common ancestor; `dissimilarity` is one minus the token overlap of
 * their claims, in [0, 1]. Both come from `core/pairs.ts`, computed over the
 * graph before any model runs: a pair the model was asked to fuse was chosen
 * because its ends sit far apart AND talk about different things, which is
 * what a synthesis worth the name needs and what a self-rated pool could not
 * promise. Absent on candidates the model nominated itself, and on everything
 * stored before this field existed.
 */
export interface BridgeBasis {
  distance: number
  dissimilarity: number
}

/**
 * The rungs a candidate synthesis can earn, in order.
 *
 * Each is a predicate over stored records — `bridgeStanding` in `core/rung.ts`
 * has the predicates and the argument for why the candidate's own `score` is
 * not among them. Declared HERE because `UntilCondition` names a rung and this
 * file cannot import that one.
 */
export const BRIDGE_RUNGS = [
  'proposed',
  'searched',
  'realised',
  'attacked',
  'cross-examined',
  'checked',
  'observed',
] as const
export type BridgeRung = (typeof BRIDGE_RUNGS)[number]

/** A candidate breakthrough: two distant branches the model thinks fuse. */
export interface Bridge {
  id: string
  a: BridgeEnd
  b: BridgeEnd
  title: string
  why: string
  /**
   * 0–1, the model's own novelty × plausibility × leverage for a pair it was
   * asked to fuse.
   *
   * ORDERS NOTHING. The tab, the `join` cue, the codec's cap and the exports
   * all order by `bridgeStanding` in `core/rung.ts`, which reads records a
   * search, another house or a person wrote. This number is the proposing
   * model's opinion of its own proposal, kept because the card prints it as
   * that and nothing more — a rating that quietly became a ranking is how the
   * tab used to work, and AGENTS.md invariant 10 is what stops it going back.
   */
  score: number
  /** The arithmetic that chose the pair, when arithmetic did. */
  basis?: BridgeBasis
  createdAt: number
  realizedNodeId?: string
  /**
   * What a link scan found, when one has been run.
   *
   * Deliberately NOT folded into `score`. The score is the model's own rating of
   * a pair it proposed; a linkage is a set of counts over what a search returned.
   * Averaging them would produce one number that is neither, and the list would
   * then sort on a figure nobody can re-derive.
   */
  linkage?: StoredLinkage
}
// `aId`/`bId` are gone rather than kept as optional aliases. As aliases every
// existing `nodes[b.aId]` would silently return undefined and five filters would
// drop every foreign candidate with no error — including the undo path's dedupe
// key, which is a template literal and would collapse to 'undefined|undefined'.

/** The id of an end, for deduping a candidate list. Local and foreign ends
 *  cannot collide: a node id and a session-scoped id are different shapes. */
export function endKey(e: BridgeEnd): string {
  return e.where === 'local' ? e.nodeId : `${e.ref.sessionId}/${e.ref.nodeId}`
}

/** Order-insensitive identity of a pair. A × B and B × A are one candidate. */
export function pairKey(b: Bridge): string {
  const k = [endKey(b.a), endKey(b.b)].sort()
  return `${k[0]}|${k[1]}`
}

/**
 * Whether a candidate can still be read.
 *
 * A local end must resolve in the graph. A foreign end is never checked against
 * anything — it carries its own copy of the label and the claim precisely so
 * that the exploration it names is allowed to be gone.
 */
export function bridgeReadable(b: Bridge, nodes: Record<string, ReasonNode>): boolean {
  const ok = (e: BridgeEnd) => e.where === 'foreign' || !!nodes[e.nodeId]
  return ok(b.a) && ok(b.b)
}

/** Whether either local end of a candidate is in the given set. */
export function bridgeTouches(b: Bridge, ids: Set<string>): boolean {
  const hit = (e: BridgeEnd) => e.where === 'local' && ids.has(e.nodeId)
  return hit(b.a) || hit(b.b)
}

/** The local end ids of a candidate — none, one, or two. */
export function localEnds(b: Bridge): string[] {
  const out: string[] = []
  if (b.a.where === 'local') out.push(b.a.nodeId)
  if (b.b.where === 'local') out.push(b.b.nodeId)
  return out
}

/** A helper for the two exports and the candidate list: what to call an end. */
export function endLabel(e: BridgeEnd, nodes: Record<string, ReasonNode>): string | null {
  if (e.where === 'foreign') return e.ref.label
  return nodes[e.nodeId]?.label ?? null
}

/**
 * Where a fork came from — see `Session.forkedFrom`.
 *
 * The source is a POINTER, not a copy of its name: the source can be renamed or
 * forgotten afterwards, and a record of what it was called then would be a second
 * copy of somebody's words with nothing keeping it true. The step and the date are
 * facts about the fork itself, and they are what tells two forks apart.
 */
export interface ForkOrigin {
  /** The exploration it was forked from, by id. */
  from: string
  /** `branch` is `forkAtStep` — the steps up to `step`, the rest left behind.
   *  `copy` is the whole graph, from a Fork door. */
  kind: 'branch' | 'copy'
  /** The step a branch was cut at, counted from 1 as the step rail counts.
   *  Absent on a copy, which carries every step. */
  step?: number
  /** When the fork was taken, epoch milliseconds. */
  at: number
}

export interface Session {
  id: string
  seed: string
  /**
   * How the seed is read — see `Frame`. Absent means `claim`, which is also what
   * every document written before frames existed meant: the old placeholder
   * demanded a claim in words, so reading absence as claim is not a default
   * standing in for a fact, it IS the fact. `claim` is normalised to absent on
   * the way to disk for the same reason.
   */
  frame?: Frame
  /**
   * The standing brief — the user's directives for this whole exploration, or
   * absent when they gave none. See `Directive`.
   *
   * User-authored, like the seed, which is why adding this field bumped `SCHEMA`
   * although absence reads fine: an older build's enumerating codec would strip
   * it on the next save, and silently losing something the user WROTE is a
   * different class of loss from losing a cache. The bump makes an old build
   * refuse the document instead.
   */
  brief?: Directive[]
  /**
   * The words the brief no longer holds — a directive removed, or the words an
   * edit replaced — each with when it joined and when it left, so the thread can
   * say when the brief changed between passes (`briefChanges` in `core/feed.ts`)
   * and an export can say a directive was edited rather than added
   * (`briefLateNote`). Written only by `setBrief`, through `briefWrite` in
   * `core/brief.ts`, and only where a pass was briefed with those words: words
   * that joined and left with no pass between steered nothing.
   *
   * Nothing a pass is told reads it — the prompts and the contract see the brief
   * as it stands — and neither does the manifest, whose brief is the one in
   * force; what an earlier pass was sent is in its receipts. Optional and read
   * defensively, so no SCHEMA bump: an older build strips it, and the thread
   * then shows the brief's arrivals without the edits and removals between them.
   */
  formerDirectives?: FormerDirective[]
  /**
   * Which project this exploration is filed under, or absent when it is unfiled.
   *
   * User-authored like the seed and the brief, which is why adding it bumped
   * `SCHEMA` although absence reads fine: an older build's enumerating codec
   * would strip the label on the next save, and a group somebody NAMED would
   * quietly dissolve into the unfiled bucket. The bump makes an old build refuse
   * the document instead.
   *
   * A label carried by the session and not a record of its own. That costs a
   * rename — renaming a project means relabelling each of its members — and buys
   * the absence of every failure a second store has: nothing to leave dangling
   * when a document is forgotten, no project able to outlive the last
   * exploration in it, and no two sources of truth for one name.
   */
  project?: string
  /**
   * The name somebody gave this exploration, or absent when they gave none — and
   * then a row is named by its seed (D105). A LABEL OVER the seed, never an edit
   * of it: the seed is the words the whole graph was reasoned from, so renaming
   * a row changes what the lists print and nothing any pass reads. Normalised
   * through `nameFor` in `core/name.ts`, which is the label clamp, so a name of
   * spaces, or one that says exactly what the seed would print, is absence.
   *
   * No `SCHEMA` bump, unlike `project` and `brief`, and that is a cost accepted
   * rather than a difference in kind: an older build's enumerating codec strips
   * it on its next save. What that loses is a display name — the row reads its
   * seed again, as it did before anyone named it — never the seed, the graph or
   * anything a pass wrote.
   */
  name?: string
  /**
   * Where this exploration was forked from, or absent for one that was begun
   * rather than forked. Written by the fork doors (`forkAtStep`,
   * `duplicateSession`, `duplicateStored`) and read by the lists, which print it
   * under the name ("branch at step 12 · 24 Sep") — four identical rows were four
   * rows nobody could tell apart. Display only: no gate, pass or figure reads it.
   * Optional and additive, so no `SCHEMA` bump: absence has always meant "not a
   * fork, or a fork from before this was recorded".
   */
  forkedFrom?: ForkOrigin
  /**
   * The versioned research workspace: evidence constitution, genealogy,
   * coverage, structured disputes, falsification portfolios, tournaments,
   * semantic revisions and audit receipts.
   *
   * Optional only for documents written before schema 10. Every fresh or
   * restored session receives one. It is kept behind a single field so an older
   * enumerating codec cannot preserve half a protocol and silently drop the
   * other half.
   */
  research?: ResearchState
  createdAt: number
  nodes: Record<string, ReasonNode>
  /** Insertion order — also the replay order for the timeline. */
  order: string[]
  bridges: Bridge[]
  ops: Op[]
  /**
   * Field sweeps: what returned pages said was unsettled about a node's
   * subject, keyed to that node, the last few kept. Reportage — no item is ever
   * a node on its own — read by a conjecture pass anywhere in the node's
   * dependency cone. See `core/sweep.ts`.
   */
  sweeps?: FieldSweep[]
  /** Disclosures survive even when the codec refuses a conflicting operation.
   * They preserve the recorded stamps and never choose an authoritative one. */
  provenanceIssues?: {
    opId: string
    issue: 'model-stamp-mismatch'
    headerModel: ModelStamp
    recordedModels: ModelStamp[]
  }[]
  /** Next durable node ordinal; never decreases when nodes are pruned or forked. */
  nextNodeOrdinal?: number
  /** Checks retained after their claim leaves the materialised graph. Entries
   * may also name a restored node after undo; readers dedupe against its card. */
  verificationArchive?: VerificationArchiveEntry[]
  /**
   * The ledger: one row per model that ran a pass here, in first-use order.
   *
   * It replaced three loose counters, because two models at two prices cannot be
   * added into one number that means anything — and this tally is the one figure
   * a user can check against a provider's own dashboard, so it has to be per
   * dashboard. A mixed-model exploration therefore announces itself by having two
   * rows rather than by producing a total nobody can reconcile.
   *
   * Searches are a row of their own inside each `Spend` and not inside its token
   * counts, because they are billed per request — which is why a single pooled
   * token figure would under-report cost by exactly the feature whose cost is
   * worth watching. Totals are computed by `totalSpend`, never stored.
   */
  spend: Spend[]
  /**
   * What has run on its own here.
   *
   * Not derivable from `ops`: an op row has no notion of a run, carries no reason
   * for its own target, and cannot hold the moves a policy declined or why a run
   * stopped — and `ops` is sliced to `MAX_OPS` on the way to disk, so a long run's
   * own account would be the first thing a reload truncated.
   */
  runs: Run[]
  /**
   * How this document came here, when it came by a share link — see `SharedCopy`.
   * Absent on everything else, which is also what an older build's enumerating
   * codec leaves behind: the copy then opens as an ordinary exploration and stops
   * saying what its link left out, and nothing the reader wrote is lost, so the
   * field needs no `SCHEMA` bump.
   */
  shared?: SharedCopy
}

/**
 * A copy opened from a share link.
 *
 * On the SESSION rather than the stored document's `ui` block because both of
 * its facts belong to the document wherever it goes: every save, fork door and
 * export spreads the session, while the `ui` block is rebuilt from the store's
 * view state on each of five save paths and would have to be threaded through
 * all of them.
 */
export interface SharedCopy {
  /**
   * The copy opens read-first: the canvas and the thread, no composer, and one
   * line saying whose it is not yet. Set when a link is opened, and cleared for
   * good by Continue working — a fork or an opened file is the reader's own
   * work from the start, so `sharedExplorationCopy` never carries it.
   */
  readFirst?: true
  /**
   * What the link that brought it left behind, counted, so each surface that
   * would have shown one can say it is missing rather than show nothing: the
   * exact model-call receipts of this many passes (their prompt text above
   * all), and this many dated whole-board states of the as-known replay. The
   * sender's file exports carry both. Stays after Continue working: it is a
   * fact about the document, not about whose it is.
   */
  left?: { receipts: number; replayStates: number }
}

/**
 * The longest project label kept.
 *
 * A project is a NAME, not a sentence: sixty characters is what a group header
 * prints beside its count without the count sliding off, and the codec cuts at
 * the same number every writer does, so what is stored is what is shown.
 */
export const PROJECT_LABEL_MAX = 60

/**
 * The ONE normalisation of a project label: trimmed, cut to `PROJECT_LABEL_MAX`,
 * and undefined when nothing is left.
 *
 * Every door that can name a project comes through here — the codec reading a
 * document off disk, `setProject`, and both fork doors naming a family — because
 * two clamps drift, and the one that drifts is always the one nobody is looking
 * at. Absence is the single representation of "no project": an empty string in
 * the field would draw a group with a blank header, and would not be equal to
 * the absence a document written before this field meant.
 *
 * Takes `unknown` so the codec can hand it a value straight out of a parsed
 * document. Cut and then trimmed AGAIN, because a label whose sixtieth character
 * is a space would otherwise group apart from the same label typed shorter.
 * Dependency-free, like everything else in this module: it imports nothing.
 */
export function projectLabel(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const cut = raw.trim().slice(0, PROJECT_LABEL_MAX).trim()
  return cut || undefined
}

/** How adventurous the expansion should be. Maps to prompt + effort. */
export type Temper = 'grounded' | 'balanced' | 'speculative'

/**
 * What kind of thing the seed is, which decides what a first-ring child IS.
 *
 * One exclusive value and not an additive directive, because the three do not
 * mix: against a claim a child supports or breaks it, against a question a child
 * is a CANDIDATE ANSWER, against a goal it is a ROUTE or a BLOCKER — and
 * `terminal` means refuted, ruled out, or blocked respectively. People type
 * questions and goals because that is how ideas arrive, and a graph that read
 * "Why do birds migrate?" as a claim spent every verb on something `challenge`
 * cannot even be aimed at.
 *
 * On the SESSION and never in `Prefs`: change the lens and the same idea is read
 * differently; change the frame and it is a different idea.
 */
export type Frame = 'claim' | 'question' | 'goal'

/**
 * One standing instruction for a whole exploration — the ADDITIVE half of the
 * brief, where `Frame` is the exclusive half.
 *
 * Four kinds and not more, each earning its place by changing what a pass does:
 * a `constraint` is a bound the user actually has, so a branch that violates it
 * is dead FOR THIS USER even where it is sound; a `given` is a premise the user
 * stipulates, which stops `challenge` attacking what they deliberately fixed; an
 * `exclude` is territory not worth their moves; a `criterion` is what would
 * settle the question for them, which points `experiment` at a real threshold.
 *
 * What was considered and rejected is part of the design: audience and tone
 * (this app emits a graph, not prose), domain (that is the lens, and a second
 * control that can disagree with the first is worse than none), depth and
 * breadth (the run budget), timeframe and money (those are constraints — they
 * do not get their own kinds).
 */
export type DirectiveKind = 'constraint' | 'given' | 'exclude' | 'criterion'

export interface Directive {
  kind: DirectiveKind
  /** The user's words, verbatim. Clamped by the codec, never rewritten. */
  text: string
  /**
   * The open knowledge gap this directive was written to answer — a node id —
   * when it came from that gap's "Answer it" rather than from the box. Nothing
   * a pass is told reads it: the model sees the directive's words under its
   * kind, exactly as a typed one. It is what lets the gap say it has been
   * answered, and take that answer back. Optional and read defensively, so no
   * SCHEMA bump; an older build strips it and the gap offers "Answer it" again,
   * with the directive itself still in the brief.
   */
  answers?: string
  /**
   * When it joined the brief (epoch ms) — stamped by the writers
   * (`stampDirectives` in `core/brief.ts`), never by a pass. A brief can change
   * mid-exploration, and an export that listed today's brief as "set for every
   * pass" put a given typed on the third day under the first day's passes; this
   * is what lets it say "added 2026-09-14, after 3 passes" instead. Like
   * `answers`, nothing a pass is told reads it. Optional and read defensively,
   * so no SCHEMA bump; absent on a directive stored before it existed, which
   * reads as "not on record" and is never guessed.
   *
   * It dates the WORDS: an edit that rewords a directive a pass was briefed with
   * stamps it again, because the passes before the edit were sent other words,
   * and the words they were sent go to `Session.formerDirectives` with their own
   * dates. The directive keeps its place in the list, and the day it first
   * joined is read back through the entry it replaced (`briefWrite`).
   */
  at?: number
}

/**
 * Words that stood in the standing brief and have left it — see
 * `Session.formerDirectives`. A directive's kind and words, and the two times
 * that bound the passes it briefed; never its gap (`answers`), which only a
 * directive in force has any use for.
 */
export interface FormerDirective {
  kind: DirectiveKind
  /** The words as they stood. */
  text: string
  /** When those words joined the brief (epoch ms) — absent where it never recorded it. */
  at?: number
  /** When they left it (epoch ms). */
  left: number
  /**
   * The words an edit put in their place, in the same directive — absent when it
   * was removed. The edited directive's own `at` is this entry's `left`, which is
   * how the two are read back as one directive reworded (`editedFrom`).
   */
  replacedBy?: string
}

export const DIRECTIVE_LABEL: Record<DirectiveKind, string> = {
  constraint: 'Constraint',
  given: 'Given',
  exclude: 'Out of scope',
  criterion: 'What would settle it',
}

/** Which discipline the model reasons from. */
export type Lens =
  | 'first-principles'
  | 'technical'
  | 'empirical'
  | 'economic'
  | 'adversarial'
  | 'systems'
  | 'historical'

export interface Filters {
  supported: boolean
  contradictions: boolean
  gaps: boolean
  insights: boolean
  disputed: boolean
  weak: boolean
  dead: boolean
  particles: boolean
  labels: boolean
}

export interface Viewport {
  x: number
  y: number
  k: number
}

/** What the reader sees for each kind. `evidence` is a study, trial or programme
 *  the model RECALLS being run, from memory and unchecked until a source check;
 *  the bare word "Evidence" dressed a recollection as a finding (D124). The
 *  reader's word for a `conjecture` is "Insight", and the kind stored as
 *  `insight` — a conclusion the model drew — reads "Takeaway", so one word never
 *  names two kinds (D333). The colour channel both wear keeps "Insight"
 *  (`CHANNEL_WORD.insight`): that names a channel, not a kind. */
export const KIND_LABEL: Record<NodeKind, string> = {
  seed: 'Seed',
  mechanism: 'Mechanism',
  evidence: 'Reported result',
  assumption: 'Assumption',
  implication: 'Implication',
  contradiction: 'Contradiction',
  gap: 'Knowledge gap',
  analogy: 'Analogy',
  experiment: 'Experiment',
  insight: 'Takeaway',
  synthesis: 'Synthesis',
  conjecture: 'Insight',
}

/**
 * What the reader sees for a node carried from a conjecture (`carriedFrom`): the
 * expectation the insight departs from, put on the graph so an ordinary source
 * check can ask whether specialists actually hold it (D279). Its stored kind is
 * `evidence`, so the check flow and invariant 2's gates treat it as any checkable
 * claim — but it is not a study anybody reported, and "Reported result" over it
 * named a sentence the insight's model wrote as a result from outside (D288). It
 * says "insight", the reader's word for a conjecture (D333).
 */
export const CARRIED_KIND_LABEL = 'What an insight departs from'

/**
 * A node's kind as the reader sees it: `KIND_LABEL`, except for a node carried
 * from a conjecture. Every surface that names a node's kind asks here, so the
 * card, the tree and the thread cannot name one node two ways. The stored kind
 * never moves: this is what it is called, not what it is.
 */
export function kindLabelOf(n: Pick<ReasonNode, 'kind' | 'carriedFrom'>): string {
  return n.carriedFrom !== undefined ? CARRIED_KIND_LABEL : KIND_LABEL[n.kind]
}

/**
 * Whether a node is a study, trial or programme the model recalls — the
 * "Reported result" of D124 — and not an expectation carried from a conjecture,
 * which shares its stored kind and is no result at all. The report's list of
 * results the model reported and the prior-work line read this, so neither
 * files a conjecture's "departs from" as something somebody measured.
 */
export function reportedResult(n: Pick<ReasonNode, 'kind' | 'carriedFrom'>): boolean {
  return n.kind === 'evidence' && n.carriedFrom === undefined
}

/** The verbs, short. Width budget: a toast (`Undid ${label}.`), a palette
 *  title, and the ~100px action buttons in the inspector. Keep in step with
 *  the OPS list there. */
export const OP_LABEL: Record<OpKind, string> = {
  expand: 'Branch',
  deepen: 'Deepen',
  challenge: 'Challenge',
  diverge: 'Diverge',
  experiment: 'Test',
  invert: 'Invert',
  bridge: 'Synthesis',
  revise: 'Rewrite',
  verify: 'Source check',
  panel: 'Panel',
  audit: 'Cross-examination',
  link: 'Link scan',
  outcome: 'Outcome',
  /** The press says "Rethink “X”", so the pass it starts is named the same in
   *  the feed, the dock and the undo toast — not "Evidence rebase", the word a
   *  reader met once, in capitals, and could not use. */
  rebase: 'Rethink',
  conjecture: 'Insight',
  priorArt: 'Prior-art search',
  sweep: 'Field sweep',
  research: 'Research analysis',
  plan: 'Programme',
  sharpen: 'Sharpen',
  survey: 'Field survey',
  portfolio: 'Portfolio',
}

/** The verb's one line, for a surface with room for one. Hoisted for the reason
 *  `LENS_LABEL` is: the inspector's tooltip and the palette's subtitle gloss the
 *  same six verbs, and two tables would let one of them drift into describing a
 *  different move under the same name. Only the six a run can be steered onto —
 *  the checks price and explain themselves where they sit. Keyed by exclusion so
 *  that a seventh derive verb stops the build until it has a line too. */
export const OP_HINT: Record<RunVerb, string> = {
  deepen: 'Decompose the mechanism',
  challenge: 'Find what breaks it',
  diverge: 'Reframe from elsewhere',
  experiment: 'What would falsify it',
  invert: 'Assume it is false',
  expand: 'Open several directions',
}

/** The verbs, completed. The overlay has a gerund for a running op; this is the
 *  form a *finished* op takes in the inspector and in an exported document.
 *  Both surfaces read this map, or they disagree about the same op. */
export const OP_PAST: Record<OpKind, string> = {
  expand: 'Branched',
  deepen: 'Deepened',
  challenge: 'Challenged',
  diverge: 'Diverged',
  experiment: 'Tested',
  invert: 'Inverted',
  bridge: 'Synthesised',
  revise: 'Re-derived',
  /** "Checked", never "Verified": a document saying a node had been *verified*
   *  would claim the one thing this pass refuses to claim. */
  verify: 'Checked',
  panel: 'Panelled',
  /** "Cross-examined", never "Refuted" or "Confirmed": the pass reports what one
   *  other model could and could not break, which is a smaller thing than either. */
  audit: 'Cross-examined',
  /** "Searched for a join", never "Found a connection". The pass reports whether
   *  a search aimed at the pair came back empty, which is a statement about the
   *  search and not about the pair. */
  link: 'Searched for a join',
  outcome: 'Re-derived from a result',
  /** The pass is "Rethink" wherever it is named (`OP_LABEL.rebase`, D184), so
   *  its past tense is that verb's: the provenance cell, the export and the
   *  pass's own trace said "Re-derived from changed evidence" after the press
   *  and the feed had stopped calling it anything of the kind. */
  rebase: 'Rethought after a changed check',
  /** "Proposed insights", never "Discovered" or "Proposed new insights": the
   *  pass set down proposals, and whether any of them is new is a search's to
   *  say. "Insight" is the reader's word for a conjecture (D333). */
  conjecture: 'Proposed insights',
  /** "Searched for prior art", never "Confirmed original": the pass reports
   *  what a search that assumed the conjecture had been said came back with. */
  priorArt: 'Searched for prior art',
  /** "Swept the field", never "Mapped the frontier": a sweep reports what
   *  the pages it returned said was unsettled, and nothing about the pages it
   *  did not return. */
  sweep: 'Swept the field',
  research: 'Structured research',
  /** \"Planned\", and the list it planned is still only a proposal until a person
   *  approves it — see `Programme`. */
  plan: 'Planned',
  sharpen: 'Sharpened',
  /** "Surveyed the field", never "Answered": the pass reports what one search
   *  returned about the question, which is a smaller thing than an answer. */
  survey: 'Surveyed the field',
  portfolio: 'Reasoned as a portfolio',
}

/**
 * One source of truth for the display defaults.
 *
 * Three copies of this literal existed — the store's boot value, the codec's
 * fallback, and the codec's per-key validator — and a fourth arrives with every
 * feature that reads a filter.
 */
export const DEFAULT_FILTERS: Filters = {
  supported: true,
  contradictions: true,
  gaps: true,
  insights: true,
  disputed: true,
  weak: true,
  dead: true,
  particles: true,
  labels: true,
}
// Every channel in the bottom bar's key is now switchable, `disputed` included.
// The old stance — no switch whose only job is hiding the strongest finding —
// gave way to a uniform key where every colour toggles; what survives of it are
// the guardrails: a node carrying a recorded outcome is NEVER hidden
// (`passesFilters`), the hidden-findings warning still says when a disputed
// node is behind any switch, and the dock badge counts everything switched off.

/** Hoisted for the reason `LENS_LABEL` is: the composer's chip and the scan
 *  panel both print the temper, and two tables would let one say "Speculative"
 *  while the other says "Reaching". */
export const TEMPER_LABEL: Record<Temper, string> = {
  grounded: 'Grounded',
  balanced: 'Balanced',
  speculative: 'Reaching',
}

/** Hoisted out of the sidebar: the panel display prints which lens said what,
 *  and a second table would let the inspector say "first-principles" while the
 *  sidebar says "First principles". */
export const LENS_LABEL: Record<Lens, string> = {
  'first-principles': 'First principles',
  technical: 'Engineering',
  empirical: 'Empirical',
  economic: 'Economic',
  adversarial: 'Adversarial',
  systems: 'Systems',
  historical: 'Historical',
}

// `READING_LABEL` and `LINK_READING_LABEL` are status words, so they are defined
// in `core/vocabulary.ts` with every other one and re-exported at the top of this
// module, where every existing import still finds them.

/** The one-line note under each, written rather than templated — each reading
 *  has its own way of being misread. */
export const LINK_READING_NOTE: Record<LinkReading, string> = {
  'already-made':
    'A search for both together returned work that addresses both. Whatever connection there is between these branches, somebody has been here before you.',
  unsound:
    'A search failed or was cut off before it finished, so nothing can be read from what it did not return. Run it again.',
  thin: 'One side returned too few results to count as a literature, so there was nothing to compare. This is usually a claim phrased too narrowly to search.',
  'no-intermediate':
    'Both literatures came back, and they share no term specific enough to carry a mechanism. They may still be connected — but not through anything these results have in common.',
  unjoined:
    'A search that went looking for the two of them together came back empty, and both literatures carry a term in common. That is a place to look, not a finding: a search finding nothing is a search finding nothing.',
}

/**
 * The note under a link scan's reading, from its record.
 *
 * `LINK_READING_NOTE` is the table; this is its one reader, because the unsound
 * line is generic and the record is not. A scan that stored which of its searches
 * did not finish (`linkStepFailure`, `engine/reason.ts`) says that, once, in the
 * note itself — it had been the generic sentence blaming "a search", then each
 * failure again as its own line, one per page that would not open. A record
 * written before the steps were named carries bare notes; those are said once
 * each, not once per occurrence.
 */
export function linkReadingNote(l: Pick<StoredLinkage, 'reading' | 'failures'>): string {
  if (l.reading !== 'unsound' || !l.failures?.length) return LINK_READING_NOTE[l.reading]
  return `${[...new Set(l.failures)].join(' ')} Nothing can be read from what it did not return.`
}

/**
 * The one-line note under each reading.
 *
 * Written, not templated: each reading has its own way of being misread and each
 * line is aimed at its own. The label is never allowed to be stronger than the
 * note under it, or the note is decoration.
 */
export const READING_NOTE: Record<SourceReading, string> = {
  supported:
    'The sources the search returned read as consistent with this. That is a record, not a proof.',
  mixed:
    "The sources don't line up cleanly: they disagree with each other, or back part of the claim and not the rest.",
  refuted:
    'Pages the model opened, on independent sites, state the opposite. The tool is reporting the record; it is not ruling on the claim.',
  'nothing-found':
    'The search ran and nothing it returned addresses this. Absence is weak evidence — a place to look, not a finding.',
  'not-checkable':
    'This is a step inside an argument rather than a statement about the world. Sources cannot settle it.',
  unsourced:
    'Every source this check named was missing from the search results, so nothing it said is carried here.',
}

/**
 * Whether a `not-checkable` reading is the gate's, set because part of the search
 * failed beside an absence, rather than the pass's own word that sources cannot
 * settle the claim. The two share the reading, the label and every count; the
 * note, the hold and the narrator's digest say which, and they ask here (D326).
 */
export function demotedForFailedSearch(v: Pick<Verification, 'reading' | 'demotedFor'>): boolean {
  return v.reading === 'not-checkable' && v.demotedFor === 'failed-search'
}

/**
 * The note under a reading, scope-aware.
 *
 * `READING_NOTE` stays the table; this is its one reader, because two of its
 * lines are FALSE on a documents-only pass — `unsourced` says "missing from the
 * search results" where no search ran at all, and `nothing-found` describes a
 * search that did — and the `not-checkable` line is false where the gate set that
 * reading because a search failed. Every surface calls this instead of indexing
 * the Record, or the app says two different things about one record.
 */
export function readingNote(
  v: Verification,
  /** Set where the stance strip (`stanceStrip`) prints beside the note — the
   *  card's check and nowhere else — so the mixed note says what the counts come
   *  to without printing them a second time. Every other surface has no strip,
   *  and keeps the counts in the sentence. */
  opts: { besideStrip?: boolean } = {},
): string {
  if (v.scope === 'corpus' && v.reading === 'unsourced') {
    return 'This check named no passage from your documents, so nothing it said is carried here.'
  }
  if (v.scope === 'corpus' && v.reading === 'nothing-found') {
    return 'Nothing in your own documents addresses this. Absence in them is not absence in the literature.'
  }
  if (v.reading === 'mixed')
    return mixedNote(stanceTally(v.sources), !opts.besideStrip) ?? READING_NOTE.mixed
  if (v.reading === 'unsourced') {
    // The count is the gate's own (`dropped` is incremented only where
    // `matchSeen` found no match), so this says exactly what the gate did.
    const n = v.dropped
    return n > 0
      ? `The check named ${n} source${n === 1 ? '' : 's'}, but none ${n === 1 ? 'was' : 'were'} in what the search returned, so ${n === 1 ? 'it was' : `all ${n} were`} set aside. That is not the same as finding nothing.`
      : 'The check named no source the search returned, so what it said is not carried here. That is not the same as finding nothing.'
  }
  // Not the pass's word but the gate's: the claim may well be one sources settle,
  // and the table's line says it is not one. The failed steps are printed beside
  // this, a line each, on the card and in the Markdown exports.
  if (demotedForFailedSearch(v)) {
    return "Some of this check's searches did not complete, so an absence here is not evidence of anything. That is not the same as finding nothing."
  }
  return READING_NOTE[v.reading]
}

/**
 * The line under a reading that says why it is not the reading the pass claimed,
 * or — on a mixed record whose sources dispute the claim — why it is not refuted.
 * Null when there is nothing to explain.
 *
 * ONE sentence for the card, both Markdown exports and the PDF, which each wrote
 * their own and had drifted: the card named a reason, the exports said only that
 * "its citations could not carry that". The refutation reason comes from
 * `refutedShortfall`, which reads the gate rather than guessing at it.
 */
export function readingCaveat(v: Verification): string | null {
  if (v.claimedReading) {
    // The unsourced reason in the note's own terms (`readingNote` above): the
    // documents where no search ran, and no claim of naming what was never named.
    const because =
      v.reading === 'unsourced'
        ? v.scope === 'corpus'
          ? 'it named no passage from your documents'
          : v.dropped > 0
            ? 'none of the sources it named were in the search results'
            : 'it named no source the search returned'
        : v.claimedReading === 'refuted'
          ? v.scope === 'corpus'
            ? 'a passage from your documents cannot carry that reading — it takes a page the check opened'
            : (refutedShortfall(v.sources) ?? 'no page it actually opened states the opposite')
          : 'some of its searches did not complete'
    return `The pass reported “${READING_LABEL[v.claimedReading].toLowerCase()}”. It is recorded as the reading above instead, because ${because}.`
  }
  if (v.reading === 'mixed') {
    const why = refutedShortfall(v.sources)
    if (why) return `Not recorded as refuted, because ${why}.`
  }
  return null
}

/**
 * A mixed reading's note, from the stances the kept sources carry.
 *
 * The fixed note said the sources "do not agree with each other" under every
 * mixed record, including one where three sources disputed the claim and none
 * supported it — sources agreeing with each other, against the claim. The
 * stances are the model's labels on sources the gate admitted, so this counts
 * what the record says; it rules on nothing, and the reading stays as it was.
 * Null when no kept source takes a side, where the table's line is the honest one.
 *
 * Two forms of one sentence, chosen by the same four shapes. `counted` is every
 * surface without a stance strip — both exports, the report, the digest the
 * narrator reads — and carries the counts, as it always has. Beside the strip
 * (the card's check) the counts are already on the line above, so the note says
 * only what they come to: whether the sources that take a side agree with each
 * other, and against or with the claim. The counted sentence under a strip
 * reading "3 dispute · 0 support" would say "The 3 sources that take a side
 * dispute this; none support it" — one count, twice, one line apart.
 */
function mixedNote(t: StanceTally, counted: boolean): string | null {
  const { supports, disputes } = t
  const n = (k: number) => `${k} source${k === 1 ? '' : 's'}`
  if (disputes > 0 && supports === 0) {
    return counted
      ? `The ${disputes === 1 ? 'source' : `${n(disputes)}`} that ${disputes === 1 ? 'takes' : 'take'} a side ${disputes === 1 ? 'disputes' : 'dispute'} this; none support it.`
      : 'Where the sources take a side it is against the claim, so they do not disagree with each other.'
  }
  // No dispute among the sources, so what made it mixed is elsewhere in the
  // check — half the claim unaddressed, or a remembered result the search could
  // not find (the verify prompt sends both there). The counts are what the record
  // can say; the summary says the rest.
  if (supports > 0 && disputes === 0) {
    return counted
      ? `${n(supports)} ${supports === 1 ? 'supports' : 'support'} this and none dispute it. The summary says what else made the reading mixed.`
      : 'Where the sources take a side it is with the claim. The summary says what else made the reading mixed.'
  }
  if (supports > 0 && disputes > 0) {
    return counted
      ? `The returned sources disagree: ${supports} support${supports === 1 ? 's' : ''} this and ${disputes} dispute${disputes === 1 ? 's' : ''} it.`
      : 'The sources that take a side disagree with each other.'
  }
  return null
}

/**
 * How the kept sources bear on the claim, counted: the check's own label on each
 * source the citation gate admitted, and the sources it gave none.
 *
 * The one count behind every surface that says how sources sided — the card's
 * and the feed's stance strip (`stanceParts`), the mixed note above, and the
 * sentence a screen reader hears when a check lands (`stanceCount`). They had
 * each filtered the list for themselves, and a count kept three times is a count
 * that can come out three ways.
 *
 * `unlabelled` is kept apart from `context` on purpose. Both read `context` in
 * `stance`, because that is the fill the live pass and the codec give a missing
 * label (`stanceUnlabelled`); a strip that called them all context would be
 * saying the check had read every one of them as relevant-but-neutral, which it
 * never said of the ones it left blank. Web sources only: a passage from your
 * documents carries no stance, and is counted in the card's Sources cell, not
 * here.
 */
export interface StanceTally {
  disputes: number
  supports: number
  context: number
  unlabelled: number
  /** Every kept web source: the four above, summed. */
  kept: number
}

export function stanceTally(sources: readonly WebSource[]): StanceTally {
  const t: StanceTally = { disputes: 0, supports: 0, context: 0, unlabelled: 0, kept: 0 }
  for (const s of sources) {
    t.kept++
    // Only beside the fill it explains: a side the gates read is never recounted
    // as unlabelled, whatever else rides on the source.
    if (s.stanceUnlabelled && s.stance === 'context') t.unlabelled++
    else if (s.stance === 'disputes') t.disputes++
    else if (s.stance === 'supports') t.supports++
    else t.context++
  }
  return t
}

/** One artefact behind a refutation: the domain that stands for it, the domains
 *  its own verified quotes actually sit on, and whether anything at all could
 *  check that it is one artefact rather than three. */
interface RefutingCluster {
  /** The first surviving member's domain. It stands for the artefact in the
   *  UNCHECKED count and in no other — see `refutedIsEarned`. */
  domain: string
  /**
   * Every registrable domain this artefact carries a VERIFIED quote on: the
   * members whose quote the client found in that page's own text, and no others.
   *
   * A set rather than a flag. A cluster-wide `quoted` boolean promoted one
   * member's evidence to the whole artefact, which let the quoted count name a
   * domain nothing had ever been checked against — the hole
   * `quotedRefutingDomainCount` closes.
   */
  quotedDomains: Set<string>
  /**
   * Whether SOMETHING could have found this artefact's copies, had there been
   * any: a member carrying a signature, or a member carrying a DOI.
   *
   * The condition for counting toward the UNCHECKED bar, and the last of the
   * syndication residue. Where no page returned its text, no member carries a
   * signature — so three verbatim copies of one wire story form three clusters
   * with nothing to collapse on, and clear a bar of three that was priced for
   * three artefacts. The raised count was never content independence: D23's
   * founding observation is that syndication produces distinct domains by
   * default, so raising the number only asks the wire desk for one more mirror.
   *
   * A DOI is the one collapse key that survives text-lessness — `sameArtefact`'s
   * first arm needs no page text at all — so a cluster carrying one is a cluster
   * whose duplicates WOULD have merged. A cluster with neither is an artefact
   * nothing in this app can distinguish from a copy of the artefact beside it,
   * and counting it is counting the domain it happens to sit on.
   */
  identityCheckable: boolean
}

/**
 * How many independent owners actually stand behind a refutation, clustered.
 *
 * ONE definition, because there are two gates — the live one in `reason.ts` and
 * the one `persist.ts` applies on the way back off disk — and they used to be
 * separate copies of the same three predicates. When the live gate got stricter
 * the disk gate did not, and its comment still said "the same demotion the live
 * path applies". Two gates that claim to be identical and are not is the worst of
 * the three states, because the weaker one is reachable by reloading the page.
 *
 * Counting registrable DOMAINS rather than sources is what makes the second one
 * cost something: two paths on one host, or two subdomains of one domain, are one
 * owner — and an owner who wrote the page also wrote the quote checked against it,
 * so `quoteFound` passes by construction and proves nothing about independence.
 *
 * DNS distinctness alone was not enough, and the hole was not hypothetical:
 * syndication publishes ONE artefact on many registrable domains, so two verbatim
 * copies of one wire story used to reach the loudest channel in the app through
 * the providers' own search, with nothing forged anywhere in the path. So both
 * counts are collapse-THEN-count: qualifying sources are clustered by artefact
 * identity first, and a cluster names ONE domain however many of them carry it.
 * `docs/decisions.md` D23 walks the old path end to end and D28 records what this
 * closes.
 *
 * The representative is the FIRST surviving source of its cluster, which is
 * deterministic and, where two clusters lead with the same host, resolves toward
 * merging — the direction `url.ts`'s asymmetry argument requires of every
 * ambiguous case here. It stands for the artefact in the UNCHECKED count alone.
 * The quoted count cannot use it, because the first member of a cluster need not
 * be a member anybody checked a quote against.
 *
 * O(n²) over pairs, which is free: the codec caps a check at six sources — which
 * is also why `refutedIsEarned` clusters twice rather than threading a flag.
 */
function refutingClusters(sources: WebSource[]): RefutingCluster[] {
  const qualifying = sources.filter(
    (s) => s.stance === 'disputes' && s.via === 'fetch' && s.quoteFound !== false,
  )

  // Transitive closure, so three copies of one story are one artefact even where
  // only consecutive pairs are close enough to match directly.
  const cluster = qualifying.map((_, i) => i)
  for (let i = 0; i < qualifying.length; i++) {
    for (let j = i + 1; j < qualifying.length; j++) {
      if (cluster[i] === cluster[j] || !sameArtefact(qualifying[i]!, qualifying[j]!)) continue
      const merge = cluster[j]!
      const into = cluster[i]!
      for (let k = 0; k < cluster.length; k++) if (cluster[k] === merge) cluster[k] = into
    }
  }

  const rows = new Map<number, RefutingCluster>()
  for (let i = 0; i < qualifying.length; i++) {
    const s = qualifying[i]!
    const domain = registrableHost(s.url)
    let row = rows.get(cluster[i]!)
    if (!row) {
      row = { domain, quotedDomains: new Set(), identityCheckable: false }
      rows.set(cluster[i]!, row)
    }
    // Only the member that was actually checked contributes its host, so a
    // verified quote can never lend its standing to the copy beside it.
    if (s.quoteFound === true) row.quotedDomains.add(domain)
    // A signature says the page's own text arrived and the collapse above could
    // read it; a DOI says which WORK this is without any text at all. Either
    // one is enough, and one member carrying it is enough — a mirror that
    // returned nothing is still that work if its sibling names the identifier.
    if (s.textHash !== undefined || s.simHash !== undefined || doiOf(s.url) !== undefined) {
      row.identityCheckable = true
    }
  }
  return [...rows.values()]
}

/**
 * The independent owners behind a refutation whose artefact something could
 * actually identify. The count `REFUTED_MIN_DOMAINS_UNCHECKED` is measured
 * against.
 *
 * NOT every qualifying owner, which is what it counted until the last of the
 * syndication residue was found. A cluster that carries neither a signature nor
 * a DOI is dropped here: it still grounds the prose of a `supports` or `mixed`
 * reading as a source, and it is still shown to the reader with its domain, but
 * it contributes NOTHING to either refuted bar. See `identityCheckable`, and
 * `docs/decisions.md` D29's second recorded correction.
 */
export function refutingDomains(sources: WebSource[]): Set<string> {
  return new Set(
    refutingClusters(sources)
      .filter((c) => c.identityCheckable)
      .map((c) => c.domain),
  )
}

/**
 * How many artefacts can each name a DISTINCT domain of their own verified text.
 *
 * The count `REFUTED_MIN_DOMAINS` is measured against, and the reason that bar
 * may be the lower one. A system of distinct representatives: every artefact in
 * the count has to point at a registrable domain no other artefact is pointing
 * at, and may only point at one of its OWN members whose quote the client found
 * in that page's own text.
 *
 * What that replaces, and why it had to go. A cluster used to keep its first
 * member's domain and a `quoted` flag promoted across the whole cluster, so an
 * artefact could be counted as verified while naming the host of a copy nobody
 * checked. Artefact one on domain A (opened, nothing checked) and domain B (quote
 * verified); artefact two on domain C (opened, nothing checked) and the same
 * domain B. The representatives are A and C, both clusters read as quoted, the
 * count is two — and every verified quote in that record came from ONE owner, B.
 * That is D5's argument one level up: whoever controls B wrote both quotes, so
 * both substring checks pass by construction, and the two-domain bar is priced
 * for two independent owners of verified text rather than one owner cited twice.
 *
 * EXACT rather than greedy, and that is not tidiness. A greedy pass takes the
 * first free domain it meets and can strand a later artefact whose only domain it
 * just spent, so the same evidence would clear the bar or miss it depending on the
 * order the sources came back in. A gate that moves with arrival order is not a
 * gate: two passes that found the same pages would hand down different verdicts,
 * and a reload would be a way to re-roll one. The size of a maximum matching is a
 * property of the graph, so it cannot move.
 *
 * Kuhn's augmenting-path assignment, which is all this needs: the codec caps a
 * check at six sources, so this searches at most six clusters.
 */
function quotedRefutingDomainCount(sources: WebSource[]): number {
  const rows = refutingClusters(sources)
    .map((c) => [...c.quotedDomains])
    .filter((domains) => domains.length > 0)

  const heldBy = new Map<string, number>()
  const claim = (row: number, tried: Set<string>): boolean => {
    for (const domain of rows[row]!) {
      if (tried.has(domain)) continue
      tried.add(domain)
      const holder = heldBy.get(domain)
      // Free, or its holder can be moved to another domain of its own — which is
      // what makes this exact rather than first-come.
      if (holder === undefined || claim(holder, tried)) {
        heldBy.set(domain, row)
        return true
      }
    }
    return false
  }

  let matched = 0
  for (let row = 0; row < rows.length; row++) if (claim(row, new Set())) matched++
  return matched
}

/**
 * Whether two sources are copies of ONE artefact rather than two.
 *
 * Three keys, weakest evidence last, and each answers the same question about a
 * different kind of copying:
 *
 *  - **The same DOI.** One work on five hosts — a preprint, a publisher landing
 *    page, a `doi.org` resolver, a PMC copy, an institutional mirror — is one
 *    source however many domains address it. The identifier says so outright, so
 *    this arm needs no text at all and works on a metadata-only row.
 *  - **The same text.** Wire-service and press-release syndication republishes
 *    BYTES, not paraphrases, so the exact-hash arm catches it at any length.
 *  - **Near the same text.** A copy under another masthead's boilerplate is the
 *    same artefact with a different wrapper, and a phrasing-specific signature is
 *    what separates that from two accounts of one subject.
 *
 * An ABSENT key never matches. Two sources that both lack a DOI are not thereby
 * the same paper, and two that both lack text are not thereby the same story —
 * which is also what keeps a document written by an older build, whose sources
 * carry no signatures at all, reading exactly as it did before.
 */
function sameArtefact(a: WebSource, b: WebSource): boolean {
  const doiA = doiOf(a.url)
  if (doiA !== undefined && doiA === doiOf(b.url)) return true
  if (a.textHash !== undefined && a.textHash === b.textHash) return true
  return (
    a.simHash !== undefined &&
    b.simHash !== undefined &&
    hamming(a.simHash, b.simHash) <= SYNDICATION_HAMMING_MAX
  )
}

/**
 * Two, and the cost of the second one is real and accepted: a genuine lone
 * authority — a retraction notice, one decisive paper — no longer reaches the loud
 * channel. Taken because `refuted` is the most destructive reading in the app
 * (`taintMap` carries it down the whole subtree) and because the failure it causes
 * is LEGIBLE — `claimedReading` keeps the claim, and the source is still shown with
 * its domain — while the failure it prevents is invisible.
 */
export const REFUTED_MIN_DOMAINS = 2

/**
 * Three, where nothing was checked against the page — and three artefacts
 * something could TELL APART, which is the half a number cannot carry.
 *
 * Where a page's own text never arrived, `quoteFound` can only ever be
 * `undefined`, so `quoteFound !== false` is vacuous and the artefact signatures
 * are absent too: the two-domain bar there was being cleared by "the model opened
 * two URLs", for the same label. The bar carries the missing evidence instead of
 * the prose carrying it alone.
 *
 * Whole PASSES land here — a provider whose search does not return page text at
 * all, which is every GPT‑5.6 tier and any custom endpoint — and so do single
 * SOURCES inside a pass that could otherwise check: a fetched PDF arrives as
 * base64 and gets no text, on a provider that returns text for every HTML page
 * beside it.
 *
 * RAISING the number was a price where a proof was needed, and on its own it did
 * not close the path it was written against. Three text-less copies of one wire
 * story carry no signature, so `sameArtefact` has nothing to compare and they
 * form THREE clusters — and D23's founding observation is that syndication
 * publishes distinct domains by default, with no attacker anywhere in the path.
 * A count of three was therefore reachable on half the registry by a press desk
 * doing its ordinary job. So the count is over `refutingDomains`, which admits
 * only clusters whose identity is checkable — a signature, or a DOI. Three
 * DOI-less text-less pages are three domains, and domains were never the thing
 * being counted.
 */
export const REFUTED_MIN_DOMAINS_UNCHECKED = 3

/**
 * Whether a `refuted` reading has earned the loudest channel in the app.
 *
 * TWO PATHS, and either one earns it:
 *
 *  - `REFUTED_MIN_DOMAINS` artefacts each naming a DISTINCT domain their quote was
 *    actually FOUND on. A verified quote is proof the page's own text arrived, and
 *    that text is what the artefact signatures were taken over — so this path
 *    carries both of the things the lower bar is priced for, per source, by
 *    construction.
 *  - `REFUTED_MIN_DOMAINS_UNCHECKED` domains over every qualifying artefact
 *    SOMETHING COULD IDENTIFY, which is the bar for opened pages with nothing
 *    checked against them. Identifiable means a signature or a DOI: on a pass
 *    where no page returns text, three verbatim syndicated copies carry neither,
 *    so they used to form three clusters and clear a bar priced for three
 *    artefacts. On that provider a refutation now needs three DISTINCT DOIs —
 *    same DOI, one cluster — which keeps the scholarly case this tool is pointed
 *    at reachable and puts DOI-less news syndication out of reach of the loud
 *    channel entirely, which is what D23 already refused as an evidence class.
 *    An attacker forging `?doi=` params now needs three domains AND three
 *    distinct DOI-shaped strings, no weaker than the pre-D28 exposure there, and
 *    the no-attacker defeat is closed.
 *
 * The two counts are counted DIFFERENTLY, and that asymmetry is deliberate. The
 * quoted one is an exact matching because a domain there is a claim about who
 * verified the text. The unchecked one is still the deduped set of first-member
 * representatives, which is order-dependent where two artefacts happen to lead
 * with the same host — and it is safe as it stands, so do not "fix" it into the
 * matcher without reading this. A set of distinct representatives IS a partial
 * matching, so that set can never be LARGER than the matching over the same
 * clusters: the error it makes is to read two artefacts as one owner, which is the
 * direction `url.ts` requires of every ambiguous case here — the one that costs a
 * true refutation its loud channel, where the reader sees the demotion, rather
 * than the one that hands a forged refutation the most destructive reading in the
 * app, where nobody does.
 *
 * The evidence class is a property of each SOURCE, and reading it off the record
 * instead was the wrong altitude. `quotesCheckable` reports what the ADAPTER
 * received — see `Verification` — so on a pass where it is `true` a fetched PDF
 * still arrives as base64 with no text, no `quoteFound` and no signature to
 * collapse on. Two of those on two domains used to clear the two-domain bar with
 * no quote checked and no content-identity defence, which is the exact
 * combination D28 and D29 exist to prevent. Now they take the higher bar, and the
 * higher bar asks them the question they can still answer without their text —
 * which work each of them is. Two DOI-bearing PDFs are two artefacts and one
 * short; two bare ones are not even countable. See D29's recorded corrections.
 */
export const refutedIsEarned = (sources: WebSource[]): boolean =>
  quotedRefutingDomainCount(sources) >= REFUTED_MIN_DOMAINS ||
  refutingDomains(sources).size >= REFUTED_MIN_DOMAINS_UNCHECKED

/**
 * Why a record whose sources dispute the claim is not `refuted`, in the words of
 * the condition that failed. Null when nothing disputes it or the gate is met.
 *
 * It EXPLAINS the gate and decides nothing: `refutedIsEarned` above is still the
 * one place the reading is earned, and this reads the same clusters and counts it
 * does rather than restating its thresholds, so the two cannot drift. A fixed
 * sentence ("no page could be opened") was the alternative, and it is false for
 * three of the four ways a refutation falls short — a lone authority, syndicated
 * copies, and pages with neither text nor a DOI — and a reader told the wrong
 * reason learns the wrong thing about the record.
 *
 * First failing condition wins, in the order a reader can act on: nothing opened,
 * then quotes not found, then too few independent owners.
 */
export function refutedShortfall(sources: WebSource[]): string | null {
  const disputing = sources.filter((s) => s.stance === 'disputes')
  if (!disputing.length || refutedIsEarned(sources)) return null
  const opened = disputing.filter((s) => s.via === 'fetch')
  if (!opened.length)
    return 'no page carrying the dispute was opened; only search results were read'
  const qualifying = opened.filter((s) => s.quoteFound !== false)
  if (!qualifying.length) {
    return 'the disputing quotes were not found in the text of the pages they were said to come from'
  }
  // Copies first. Two verified copies of one wire story sit on two sites, so
  // "only one site carries a disputing quote" below was false about them — the
  // quote was checked on both — and the reason was the collapse, not the count.
  const clusters = refutingClusters(sources)
  if (clusters.length < qualifying.length) {
    return `the ${qualifying.length} pages that dispute it are copies of ${clusters.length === 1 ? 'one article' : `${clusters.length} articles`}, and copies of one article count once`
  }
  // Below the bar of two, so at most one — and one owner who wrote the page also
  // wrote the quote checked against it (D5).
  if (quotedRefutingDomainCount(sources) > 0) {
    return `only one site carries a disputing quote that was checked against its own page, and it takes ${REFUTED_MIN_DOMAINS}`
  }
  // Only artefacts something could identify count toward the unchecked bar
  // (`identityCheckable`), and they count by owner, so two DOIs on one resolver
  // are one site: "no text or DOI" and "too few sites" are different failures.
  if (!clusters.some((c) => c.identityCheckable)) {
    return 'the pages that dispute it returned no text or DOI, so nothing could tell them apart from copies of one another'
  }
  const owners = refutingDomains(sources).size
  const sites = `${owners} independent site${owners === 1 ? '' : 's'}`
  return clusters.filter((c) => c.identityCheckable).length > owners
    ? `the pages that dispute it sit on only ${sites}, and with no quote checked against a page that takes ${REFUTED_MIN_DOMAINS_UNCHECKED}`
    : `only ${sites} dispute${owners === 1 ? 's' : ''} it with no quote checked against the page, and that takes ${REFUTED_MIN_DOMAINS_UNCHECKED}`
}

/**
 * What is actually behind a `refuted` reading, in one sentence.
 *
 * Two strengths of evidence wear one label and must not read as one. Returns
 * null for every other reading: only `refuted` is gated on an opened page, so
 * only `refuted` has a gate whose strength varies by model.
 *
 * THREE branches, not two. A harvester sets a source's text only when the
 * document arrived as text; a fetched PDF arrives as base64 and gets nothing, so
 * `quoteFound` comes back `undefined` on a provider that CAN return text. A
 * two-way split would assert a verified quote for that case, which is this
 * function's own failure mode.
 *
 * The order fails toward the WEAKER claim in every ambiguous case, including an
 * absent `quotesCheckable` off a hand-edited document.
 */
export function refutedBacking(v: Verification): string | null {
  if (v.reading !== 'refuted') return null
  // The gate's own count of sites carrying a verified quote, each its own
  // artefact. At two or more — the quoted path's bar — it is said, because "the
  // sentence it quoted was found in that page's own text" read as one page under
  // a reading no one page can earn.
  const quoted = quotedRefutingDomainCount(v.sources)
  if (quoted >= REFUTED_MIN_DOMAINS) {
    return `Each quoted sentence was found in its own page’s text, on ${quoted} independent sites.`
  }
  if (v.sources.some((s) => s.stance === 'disputes' && s.quoteFound === true)) {
    return 'The sentence it quoted was found in that page’s own text.'
  }
  if (v.quotesCheckable === false) {
    return 'The pages were opened, but this model’s search does not return their own text, so the quoted sentence could not be checked against any of them. Read this as “pages that were opened say otherwise”, not as a verified quote — which is why a reading like this one needs three independent domains behind it rather than two, each naming a different work by its DOI, since without page text an identifier is the only thing that can say whether these are three sources or one republished three times.'
  }
  return 'The page was opened, but its text did not come back in a form that could be read, so the quoted sentence could not be checked against it.'
}

/**
 * How far into a source this check actually got. Three states, one definition.
 *
 * Gated on `quotesCheckable` and NEVER on `via` alone: on a provider that opens a
 * page without returning its text, `via === 'fetch'` is true and "read in full" is
 * false. The screen and the export both call this, so the two cannot come to
 * describe one source in two ways.
 */
export function sourceReach(s: WebSource, v: Verification): string {
  if (s.via !== 'fetch') return 'from the search result only'
  return v.quotesCheckable === false ? 'opened, page text not returned' : 'read in full'
}

/** Printed wherever a documents-only check is reported. One string, because the
 *  sidebar and the inspector already had two copies of this sentence drifting. */
export const CORPUS_ONLY_CAVEAT =
  'This pass read only your own documents. Absence in them is not absence in the literature.'

/**
 * The miss, in words: the documents were searched, nothing in them cleared the
 * floor, and the check went to the web. The card's provenance line and the
 * exported report both print it from here, for the same reason as the caveat
 * above — one sentence, so the two cannot come to say different things.
 */
export function corpusMissNote(v: Verification): string | null {
  const c = v.corpusSearched
  if (!c) return null
  return `your ${c.docs} document${c.docs === 1 ? '' : 's'} searched, none matched closely enough — this check used the web${c.preferCorpus ? ', even with Prefer my sources on' : ''}`
}

/** The merge key for the ledger. Provider-qualified, so two providers shipping
 *  the same id can never collapse into one row. */
export const spendKey = (m: ModelStamp): string => `${m.provider}/${m.id}`

/**
 * One receipt folded into a ledger — the ONLY place that arithmetic is written.
 *
 * It lived inside `addSpend` while there was one way to reach the ledger. There
 * are two now: the open exploration's, held in memory, and a closed one's, read
 * off disk and written back, for the single channel that can outlive the
 * exploration that paid for it. Two ledgers reachable by two copies of the same
 * summing is exactly the shape this codebase has been bitten by before, so the
 * sum is here and both call it.
 *
 * Appends on first use of a model and merges on `spendKey` after that, so the
 * ledger stays one row per model. Pure — the input array is not touched.
 *
 * `spentNothing` above rides with it for the same reason. A pass rejected before
 * it ran bills nothing, and `ModelError.spent` carries those zeroes faithfully;
 * recording them opens a ledger row reading "0 in · 0 out", which says a model
 * ran and cost nothing and reads as a bug rather than as the truth it is. Both
 * writers have to refuse it, so the refusing is written once.
 */
export function spentNothing(row: Spend): boolean {
  return !row.inputTokens && !row.outputTokens && !row.searches
}

/**
 * A row's breakdown, or the honest stand-in for one that has none.
 *
 * A row with no `detail` is not a row of zeroes — it is a row recorded before
 * the meter existed, or by a build that did not have one. Its aggregates are
 * real and must survive the merge, so they arrive as unclassified. Reading them
 * as zero would quietly shrink the total; claiming them as uncached input would
 * invent a category the provider never reported.
 */
export const detailOf = (row: Spend): SpendDetail =>
  row.detail ?? {
    inputUncached: 0,
    cacheWrite5m: 0,
    cacheWrite30m: 0,
    cacheWrite1h: 0,
    cacheRead: 0,
    inputUnclassified: row.inputTokens,
    webSearches: 0,
    webFetches: 0,
    serverRequestsUnclassified: row.searches,
  }

/**
 * Two receipts' breakdowns, summed — or nothing, when neither had one.
 *
 * Returning `undefined` for two legacy rows is what keeps absence meaningful: a
 * ledger that has never seen a detailed receipt goes on saying so, rather than
 * growing an all-unclassified breakdown that looks like a measurement.
 */
export function mergeDetail(a: Spend, b: Spend): SpendDetail | undefined {
  if (!a.detail && !b.detail) return undefined
  const x = detailOf(a)
  const y = detailOf(b)
  return {
    inputUncached: x.inputUncached + y.inputUncached,
    cacheWrite5m: x.cacheWrite5m + y.cacheWrite5m,
    cacheWrite30m: x.cacheWrite30m + y.cacheWrite30m,
    cacheWrite1h: x.cacheWrite1h + y.cacheWrite1h,
    cacheRead: x.cacheRead + y.cacheRead,
    inputUnclassified: x.inputUnclassified + y.inputUnclassified,
    webSearches: x.webSearches + y.webSearches,
    webFetches: x.webFetches + y.webFetches,
    serverRequestsUnclassified: x.serverRequestsUnclassified + y.serverRequestsUnclassified,
  }
}

/**
 * Two receipts on one model, summed.
 *
 * The ONE place a receipt is added to another, so the per-model ledger and the
 * per-op row cannot come to disagree about what summing means — `addSpend`
 * attributes the same write twice and calls this for both.
 */
export function addSpendRow(into: Spend, row: Spend): Spend {
  const detail = mergeDetail(into, row)
  // `row` is one receipt, so its own total is its largest request. The row it
  // joins knows its largest only if it recorded one; unknown there leaves the
  // sum unknown, and an unknown row is priced by its total — upward, never down.
  const peak =
    into.peakInputTokens === undefined
      ? undefined
      : Math.max(into.peakInputTokens, row.peakInputTokens ?? row.inputTokens)
  return {
    model: into.model,
    inputTokens: into.inputTokens + row.inputTokens,
    outputTokens: into.outputTokens + row.outputTokens,
    searches: into.searches + row.searches,
    ...(detail ? { detail } : {}),
    ...(peak !== undefined ? { peakInputTokens: peak } : {}),
  }
}

/**
 * One receipt, with its largest request recorded — its own input, since a
 * receipt is one request. (A paused search pass resumed within one receipt sums
 * its turns, which can only overstate the largest; no row with a long-context
 * tier pauses.) Where a receipt starts a row, so the row knows its peak from its
 * first write. See `Spend.peakInputTokens`.
 */
export const withPeak = (row: Spend): Spend =>
  row.peakInputTokens !== undefined ? row : { ...row, peakInputTokens: row.inputTokens }

export function mergeSpend(rows: readonly Spend[], row: Spend): Spend[] {
  const key = spendKey(row.model)
  const i = rows.findIndex((r) => spendKey(r.model) === key)
  if (i === -1) return [...rows, withPeak(row)]
  return rows.map((r, j) => (j === i ? addSpendRow(r, row) : r))
}

/** Which ledger a receipt belongs on, or neither. See `spendGoesTo`. */
export type SpendDestination = 'open' | 'stored' | 'nowhere'

/**
 * Which ledger a receipt belongs on.
 *
 * Asking takes no lock, so it is the one caller whose tokens can land after its
 * exploration has been put away, and the payer it carries is what says where the
 * row goes. It was the id alone, and an id is not a document: `s_demo` is fixed
 * and deliberately REUSED, so an answer paid for on one worked example and
 * settling after that example has been forgotten and replayed found its own name
 * open and charged the fresh one — a ledger claiming, with a model stamp, work
 * that belonged to an instance the reader deleted.
 *
 * So the payer names the document: the id AND the moment that document began.
 * `createdAt` is set once by `emptySession` and carried unchanged through
 * everything that keeps a document while changing its name — the remint above
 * all, which is why the rename can go on rewriting `id` without disturbing this.
 * Replacing the document is exactly what does move it, because `startSession`
 * builds a new one. The codec keeps its side of this too: `createdAt` is the one
 * time in a stored document read as written rather than through the
 * document-wide clock translation, because a translated value moves between two
 * reads whenever the save clock was ahead, and an identity that moves is no
 * identity (D79).
 *
 * The same name and a different beginning resolves to NOWHERE rather than to the
 * stored shelf, and that is D38's rule rather than a new one: a forgotten
 * exploration took its ledger with it, and putting the row somewhere else is the
 * defect being removed rather than a fallback. The accepted cost is a real
 * payment that appears on no ledger — which leaves the figure reading LOW for a
 * document nobody can open, rather than HIGH for one they are looking at.
 *
 * Two documents sharing an id AND a millisecond would still be confused. Only
 * `s_demo` can share an id, and replaying it takes seconds.
 */
export function spendGoesTo(
  open: { id: string; createdAt: number },
  paidBy: { id: string; born: number },
): SpendDestination {
  if (open.id !== paidBy.id) return 'stored'
  return open.createdAt === paidBy.born ? 'open' : 'nowhere'
}

/** Derived, never stored. The sidebar and the export front matter both read it,
 *  so one pooled figure cannot drift from the rows it came from. */
export function totalSpend(s: Session): {
  inputTokens: number
  outputTokens: number
  searches: number
} {
  let inputTokens = 0
  let outputTokens = 0
  let searches = 0
  for (const r of s.spend) {
    inputTokens += r.inputTokens
    outputTokens += r.outputTokens
    searches += r.searches
  }
  return { inputTokens, outputTokens, searches }
}

/** Every model that reasoned in this graph, in first-appearance order. Computed
 *  from the ledger, which is the one list a truncated `ops` array cannot
 *  shorten. */
export function modelsUsed(s: Session): ModelStamp[] {
  return s.spend.map((r) => r.model)
}

/**
 * The models named as a sentence fragment: `A`, `A and B`, `A, B and C`.
 *
 * Written once because three surfaces name this list — the sidebar, both exports —
 * and joining with `' and '` reads as broken English the moment a graph holds
 * three, which four rows in the registry makes reachable. No count word either:
 * the list IS the count, and a spelled numeral is one more thing to get wrong.
 */
export function modelNames(models: ModelStamp[]): string {
  const n = models.map((m) => m.name)
  if (n.length <= 1) return n[0] ?? ''
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`
}

// `BEARING_LABEL` — which way a recorded result went — is a status word too, and
// lives in `core/vocabulary.ts`; it is re-exported at the top of this module.

/* ── channel resolution: written ONCE, here ───────────────────────────────
   Four functions, one ordering, and every consumer picks the one that answers
   its own question. Nothing below allocates: the renderer, the minimap and the
   label overlay all call into here per node per frame. */

/**
 * What kind of reasoning move this is, ignoring every epistemic overlay.
 *
 * This is the old `liveChannelOf` body under its own name, because three things
 * need the kind as a CATEGORY rather than as a colour: the four display
 * switches, the sidebar tallies, and every node glyph. A switch labelled
 * "Contradictions" that leaves a contradiction on screen because a source
 * disputed it is a switch you can no longer trust — and since hiding a node
 * hides its whole subtree, that is worse than a mislabelled switch.
 *
 * Two of its rules read the model's self-ratings — tension past 0.62 wears the
 * contradiction's cross, confidence under 0.4 the weak hue — and a claim the
 * reader rewrote has none (`selfRatingsOf` in `rewrite.ts`, which this module
 * cannot import): those numbers rated the words she replaced. Its channel is its
 * kind's, and nothing else, as the Overview already clears it.
 */
export function kindChannelOf(n: ReasonNode): Channel {
  // A conjecture reads in the insight channel whatever its self-ratings say, and
  // ahead of the tension rule. It departs from an expectation by construction,
  // so a high `tension` is the departure it was asked for, not the graph arguing
  // with itself — read as a contradiction it would draw a cross, count as an
  // open contradiction, and turn up in its own WHAT DOES NOT FIT block. And its
  // honest confidence is low by design, so the `weak` rule below would put every
  // one in the channel for claims the reasoning barely supports. Never a channel
  // of its own: the hues each mean one thing, and a new one would be a verdict
  // in colour. Its openness is carried by the glyph instead; see `hollowGlyph`.
  if (n.kind === 'conjecture') return 'insight'
  const rated = n.editedAt === undefined
  if (n.kind === 'contradiction' || (rated && n.tension > 0.62)) return 'tension'
  // An experiment is a hollow ring because an open question has no centre yet.
  // Once its result is in it has one, so an answered experiment leaves the gap
  // channel and reads by its confidence. The glyph filling in is the cheapest
  // true thing this feature can say.
  if (n.kind === 'gap' || (n.kind === 'experiment' && !n.outcome)) return 'gap'
  if (n.kind === 'insight' || n.kind === 'synthesis') return 'insight'
  if (rated && n.confidence < 0.4) return 'weak'
  return 'signal'
}

/**
 * Whether a node draws as a hollow ring rather than a filled disc.
 *
 * One answer for both cameras, because two renderers deciding it separately is
 * how the flat and spatial views would come to disagree about the same node.
 * An open question has no centre yet, and neither does a conjecture nothing
 * outside the model has tested: a recorded result fills it in, exactly as a
 * result fills in an experiment's ring. The insight channel's containing ring
 * still draws around it, so an untested conjecture reads as an open proposal —
 * never as a settled insight, and never as a gap.
 */
export function hollowGlyph(n: ReasonNode): boolean {
  return kindChannelOf(n) === 'gap' || (n.kind === 'conjecture' && !n.outcome)
}

/**
 * Whether a verification still describes the words on the node.
 *
 * A rewrite replaces the claim and never re-checks it, so a check taken before
 * the rewrite was a check of different words — and a refuted hue left on the
 * canvas after that would be the tool asserting something about text nobody
 * ever searched for. New ledger rows also carry the exact checked wording: undo
 * can restore older text while retaining a later paid check, and timestamps
 * alone would make that later row look live on the wrong claim.
 */
export function verificationLive(n: ReasonNode): boolean {
  const v = n.verification
  if (!v) return false
  if (v.claimHashAtCheck !== undefined) {
    return v.claimHashAtCheck === textHash(foldText(n.claim))
  }
  if (v.claimAtCheck !== undefined) return foldText(v.claimAtCheck) === foldText(n.claim)
  // Only legacy checks lack a wording identity. Their timestamp remains the
  // conservative fallback, and restored clocks are deliberately settled.
  return !(n.editedAt !== undefined && v.checkedAt < n.editedAt)
}

/**
 * Whether anything was actually checked against a source.
 *
 * NOT `!!n.verification`. A `not-checkable` reading and an `unsourced` demotion
 * both write a record and neither checked anything — one because sources cannot
 * settle the claim, or because its absence stood beside a failed search (D326),
 * the other because every source it named was fabricated.
 * Letting either wear the canvas mark meaning "checked against sources", or
 * count in "12 nodes carry a source check", is the most direct way this design
 * could let someone believe the model checked something it did not.
 */
export function checkedAgainstSources(n: ReasonNode): boolean {
  const r = n.verification?.reading
  return r === 'supported' || r === 'mixed' || r === 'refuted' || r === 'nothing-found'
}

/**
 * The withdrawn works a node's live check cites, one passage per work, in the
 * order the check cited them.
 *
 * Read off the stored `notice`, never the title — the title carries " —
 * RETRACTED" too, and so does a retraction notice's own title, which is the
 * paper correctly citing the withdrawal (D31). By work, because one paper quoted
 * three times is one thing a publisher took back. Live checks only: a check of
 * an earlier wording is not what the claim now rests on. The coach's `retracted`
 * cue and every status line read this one rule.
 */
export function retractedWorks(n: ReasonNode): PassageCite[] {
  if (!verificationLive(n)) return []
  const seen = new Set<string>()
  const out: PassageCite[] = []
  for (const c of n.verification?.corpus?.cites ?? []) {
    if (c.notice !== 'retracted' || seen.has(c.docId)) continue
    seen.add(c.docId)
    out.push(c)
  }
  return out
}

/**
 * The channel a node would read as if the path were still open.
 *
 * A terminal node still *is* a contradiction or a gap — that is why the branch
 * died. The growth animation shows this colour first and only then fades it to
 * grey, so you watch the path be explored and then exhausted rather than
 * arriving pre-dead.
 */
/**
 * A node id as a model may echo it back, normalised to the id itself.
 *
 * Prompts print a claim's id in square brackets — `### [vac-casimir] Label` — so
 * a model asked to copy "the id" reasonably copies `[vac-casimir]`. The first
 * live cross-examination did exactly that and five correct verdicts were thrown
 * away for it. The client chose the delimiter, so the client strips it.
 *
 * Only the ENDS are stripped: brackets inside an id are part of the id, and a
 * normaliser that ate them would corrupt a real reference to fix a cosmetic one.
 */
export function normalizeNodeRef(raw: string): string {
  return raw
    .trim()
    .replace(/^\[+|\]+$/g, '')
    .trim()
}

/**
 * Which house a branch's claims belong to, for choosing who cross-examines it.
 *
 * The selected node's own stamp wins — it is the claim you pointed at. Failing
 * that, the house that wrote the most of the branch, so a subtree grown entirely
 * by one model is audited by the other even when the node you clicked predates
 * stamps. Failing that (a wholly unstamped branch, which every demo node is),
 * the model now selected, because that is who would have written it.
 */
export function branchAuthor(
  root: ReasonNode,
  targets: readonly ReasonNode[],
  fallback: ProviderId,
): ProviderId {
  if (root.model) return root.model.provider
  const counts = new Map<ProviderId, number>()
  for (const t of targets) {
    const p = t.model?.provider
    if (p) counts.set(p, (counts.get(p) ?? 0) + 1)
  }
  let best: ProviderId | null = null
  let most = 0
  for (const [p, c] of counts) {
    if (c > most) {
      best = p
      most = c
    }
  }
  return best ?? fallback
}

/**
 * The claims an auditor is allowed to rule on: everything it did not write.
 *
 * Resolving one auditor for a whole branch is not enough on a graph that two
 * models have worked. A branch can hold claims from both houses, and the ones
 * the auditor authored would otherwise be handed back to it — which is the panel
 * again, counted into a tally whose entire claim is that a DIFFERENT model could
 * not break them.
 *
 * An unstamped claim has no author and is therefore always auditable: the seed
 * and every demo node fall here, and excluding them would empty most branches.
 *
 * A REWRITTEN claim reaches the same place by the same logic. `editedAt` means
 * the words on screen are the user's, whatever stamp the node still carries —
 * the stamp records who drafted a sentence that is no longer there — so the skip
 * has nothing left to protect: handing the auditor text a person typed is not
 * the panel again, and withholding it would keep the one claim the user thought
 * worth rewriting away from the only model that could attack it.
 *
 * The rewrite is read the OPPOSITE way one file over, and both readings are
 * right off the one fact. The calibration collectors in `persist.ts` DROP a
 * rewritten node, because grading a model against a confidence a person typed
 * would be scoring the wrong author. Authorship moves to the user in both
 * places; it just excludes a different party each time — there, the model being
 * measured, and here, nobody, because the auditor is not the author any more.
 */
/**
 * Whether a claim's words are open to examination by THIS house.
 *
 * Two ways in, and the edit is the interesting one: a model may not examine
 * what it wrote, but once a person has rewritten the claim the words are
 * theirs, and any house may be asked about them. Taken apart from
 * `auditableBy` so the persistence doors can apply the same rule to an audit
 * arriving from outside — a `pass` from the node's own house is not a
 * cross-examination, and the `cross-examined` rung says in as many words that
 * ANOTHER house examined it.
 */
export const auditableByProvider = (
  model: ModelStamp | undefined,
  editedAt: number | undefined,
  auditor: ProviderId,
): boolean => editedAt !== undefined || model?.provider !== auditor

export function auditableBy(targets: readonly ReasonNode[], auditor: ProviderId): ReasonNode[] {
  return targets.filter((n) => auditableByProvider(n.model, n.editedAt, auditor))
}

/**
 * Whether an audit still describes the claim it examined.
 *
 * The same rule as `verificationLive`, for the same reason: a rewrite replaces
 * the words, and an objection left standing against text nobody examined would
 * be the tool asserting something it never checked.
 */
/**
 * The marks a claim carries wherever the model is shown it.
 *
 * `line()` in `engine/prompts.ts` prints these beside the label in every turn
 * that lists claims, and the plan turn is one of them — so they are inputs to
 * the list a person is asked to approve, exactly as `evidenceMarks` below is.
 * `measured:` is the loudest of them: a recorded result is the one thing in the
 * model's context that is not its own prose, and a step proposed where no
 * measurement existed is a different step once one does.
 *
 * Here so `programmeClaimKey` can stamp what the turn showed without `core`
 * importing the engine at runtime, and so the printer and the stamp read one
 * expression. The stamp missed this once already: it was taught `evidenceMarks`
 * and not the marks printed one line above them.
 */
export const claimMarks = (n: ReasonNode): string[] => {
  const marks: string[] = [n.kind]
  if (n.terminal) marks.push('dead end')
  if (n.editedAt) marks.push('user-revised')
  // `outcomeLive`, and this is the mark that most needed it. The comment above
  // calls `measured:` the loudest of them — a recorded result is the one thing
  // in the model's context that is not its own prose — so printing it against a
  // claim the record does not name tells the planner an experiment has been run
  // on a proposition nobody tested, and `buildPlanTurn`'s rule is never to
  // propose a step where the graph already has one.
  //
  // The stamp reads the same expression, so a list approved while this printed
  // is retired when it stops printing, which is `programmeClaimKey`'s whole job.
  if (n.outcome && outcomeLive(n)) marks.push(`measured: ${n.outcome.bearing}`)
  return marks
}

/**
 * What a claim has ALREADY been through, in the words the ladder uses.
 *
 * `buildPlanTurn` prints this under every live claim and the prompt tells the
 * model what it means — never propose `verify` on a claim already checked, never
 * `audit` on one already cross-examined. So the list a person is approving is a
 * function of these three records as much as of the wording, and
 * `programmeClaimKey` stamps it with exactly this string for that reason: a
 * source check run between planning and approval retires the `verify` step that
 * was planned in ignorance of it, and a stamp that could not see the check left
 * the sheet calling the list fresh and spending on a pass the graph already had.
 *
 * Here rather than in `engine/prompts.ts` so the stamp can read it without
 * `core` importing the engine at runtime — the cycle hazard `core/models.ts`
 * names. One expression, two callers, so the two cannot answer differently
 * about one node.
 */
export const evidenceMarks = (n: ReasonNode): string => {
  const marks: string[] = []
  if (n.verification && verificationLive(n)) marks.push(`checked: ${n.verification.reading}`)
  if (n.audit && auditLive(n)) marks.push(`cross-examined: ${n.audit.verdict}`)
  if (n.panel) marks.push('panelled')
  return marks.join(', ')
}

/**
 * Whether a recorded outcome still bears on the claim as it now stands.
 *
 * The fourth evidence record to get a liveness rule, and the only one whose rule
 * is NOT `verificationLive`'s — the difference is the whole of it.
 *
 * `verificationLive` and `auditLive` fall back to comparing timestamps when a
 * record carries no wording identity, and `panelLive` is nothing but that
 * comparison. This does not, because the file had already decided that case and
 * decided it the other way: *"the observation is the world's and survives a
 * rewrite, as it does on the canvas"* — the comment on the invariant test that
 * catches anyone who changes it. An observation is a result about the WORLD, not
 * a reading of a model's sentence, and a rewrite is as often a tightening as a
 * replacement. Throwing away the one record in this app that did not come from a
 * model, on the suspicion of a timestamp, is the wrong way to be wrong.
 *
 * A SNAPSHOT is not a suspicion. `Outcome.claimAtRecord` is "the exact
 * proposition this observation was recorded against", written for this question
 * and already read by the tournament ledger, so where it differs from the claim
 * we are not guessing that the record has drifted — we are reading that it was
 * recorded against something else. That is the case this predicate exists for,
 * and it is reachable both through a field-wise merge, which pairs one branch's
 * rewritten synthesis with the other's outcome and raises no conflict, and
 * locally, since a rewrite leaves the record standing.
 *
 * `foldText` and not `===`, matching `verificationLive`, so a claim that came
 * back through the codec with its whitespace normalised is the same claim.
 *
 * The loudest half is `refuted`. A record read against the wrong proposition
 * does not merely award a rung nothing earned — it STOPS a candidate, and
 * `rung.ts` sorts every stop below every live one.
 */
export function outcomeLive(n: ReasonNode): boolean {
  const o = n.outcome
  if (!o) return false
  // Absent only on documents written before outcome snapshots existed, and there
  // the decision above stands: the observation survives.
  if (o.claimAtRecord === undefined) return true
  return foldText(o.claimAtRecord) === foldText(n.claim)
}

export function auditLive(n: ReasonNode): boolean {
  const a = n.audit
  if (!a) return false
  return !(n.editedAt !== undefined && a.at < n.editedAt)
}

/**
 * Whether a panel checked at `checkedAt` still describes a claim last edited at
 * `editedAt`.
 *
 * The panel's rule is not `auditLive`'s, however alike the arithmetic looks, and
 * the difference is the reason this takes two numbers rather than a node. An
 * audit and a source check SURVIVE a rewrite on purpose — the pass really
 * happened, and `auditLive` / `verificationLive` let the record stand while its
 * hue does not. A panel is CLEARED by one: `reason.ts` writes `panel: undefined`
 * in the same patch as `editedAt`, because three lenses ruled on wording that is
 * no longer there. So `checkedAt < editedAt` is a state the app never writes,
 * and every panel in it came from somewhere else — a build that did not clear
 * it, a hand-edited document, or a field-wise merge that paired one branch's
 * newer claim with the other's older panel and saw no conflict.
 *
 * Two numbers rather than a `ReasonNode` because the two DOORS hold raw records
 * and no node yet, and they are the readers that matter: this rule was restated
 * at four sites — the merge, the tournament's ledger, the panel collector, and
 * nowhere in the codec — which is how `bridgeStanding` came to award `attacked`
 * off a panel every other reader already knew was stale.
 */
export const panelLive = (checkedAt: number, editedAt: number | undefined): boolean =>
  editedAt === undefined || checkedAt >= editedAt

/**
 * A value out of a map, by a key that came from OUTSIDE.
 *
 * `nodes[id]` on a plain object walks the PROTOTYPE when the key misses, and a
 * stored document names its own keys: a claim whose `parentId` is `"toString"`
 * finds `Object.prototype.toString`, which is a function and therefore truthy.
 * Every "does this node exist" check in this codebase is that truthiness test,
 * so the phantom passes them all — `validateTopology` stops calling the parent
 * missing, `asEdges` keeps a dangling edge, `asBridgeEnd` admits an end naming
 * nothing — and the first reader to dereference it reads `.claim` off a function
 * and throws, which is a malformed document crashing a merge instead of being
 * refused by it.
 *
 * `hasOwnProperty.call` rather than `key in map`, because `in` walks the
 * prototype too, and rather than `Object.hasOwn` only to match the four sites
 * that already spell it this way. The maps cannot simply be given a null
 * prototype instead: they are built in a dozen places and every `{...spread}`
 * of one hands back an ordinary object, so the guard has to live at the LOOKUP,
 * where it can be read and tested.
 *
 * Only for keys that came off disk, out of a snapshot, or out of a model's
 * answer. A key from `Object.keys` is already an own key and saying so twice
 * would just be noise.
 */
export const ownEntry = <T>(map: Record<string, T>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined

/**
 * Can this string be an id at all?
 *
 * The WRITE half of `ownEntry`, and the one that loses data rather than
 * inventing it. `map[key] = value` on a plain object is an ordinary assignment
 * for every string but one: `"__proto__"` runs the legacy prototype setter, so
 * no own property appears and the entry is gone with nothing thrown. A read then
 * reports the id missing, and every check that asks "is it there" agrees.
 *
 * That is worse than the lookup hazard, because it is silent in the direction of
 * LOSS: a snapshot arrives with a claim, the copy comes out without it, and a
 * hash recomputed over the copy matches. Refused rather than special-cased,
 * because carrying such an id would mean `defineProperty` at every write in this
 * codebase and one plain assignment added later would put the hole back.
 *
 * Nothing this app writes can produce one — ids come from `uid()` — so the cost
 * is a hand-edited document being turned away, which is what the import door
 * does with every other unrepresentable shape.
 */
export const storableId = (key: string): boolean => key !== '__proto__'

/**
 * Completed reviews with no specific objection. Field names remain compatible
 * with historical consumers; neither count establishes validity.
 *
 * Counted here, mechanically, and never asked of a model — the same rule as the
 * panel tally in `panel.ts`. A model asked "how much of this held up?" would be
 * summarising its own work, and the one number this feature exists to produce
 * would become another self-report.
 */
export function auditSurvival(s: Session): { examined: number; survived: number } {
  const t = auditTally(
    s.order.flatMap((id) => {
      const n = s.nodes[id]
      return n && auditLive(n) ? [n.audit!] : []
    }),
  )
  return { examined: t.examined, survived: t.noObjection }
}

/** What a set of scoped reviews came to, by verdict — the one arithmetic the
 *  export's sentence and the card's batch line both print (`auditTallySentence`
 *  in `core/auditReview.ts`). The caller decides which reviews: live ones only. */
export interface AuditTally {
  /** Reviews that reached a verdict: no specific objection, or a criticism. */
  examined: number
  /** …of which recorded no specific objection. */
  noObjection: number
  /** …of which proposed a criticism, unchecked until its argument is. */
  objections: number
  /** Reviews that could not justify a verdict from the supplied context, and
   *  stay outside `examined`. */
  needsContext: number
  /** The reviewing models, by name, first-seen order. */
  auditors: string[]
}

/** Counted, never asked: see `auditSurvival` above. */
export function auditTally(audits: readonly Audit[]): AuditTally {
  const t: AuditTally = {
    examined: 0,
    noObjection: 0,
    objections: 0,
    needsContext: 0,
    auditors: [],
  }
  for (const a of audits) {
    if (!t.auditors.includes(a.model.name)) t.auditors.push(a.model.name)
    if (a.verdict === 'insufficient-context') {
      t.needsContext++
      continue
    }
    t.examined++
    if (a.verdict === 'pass') t.noObjection++
    else t.objections++
  }
  return t
}

export function liveChannelOf(n: ReasonNode): Channel {
  // `outcomeLive`, beside the `verificationLive` on the line under it. The name
  // of this function is the promise: a channel read from records that still
  // describe the claim. The outcome was the one it did not keep, and this is the
  // reader where it travels furthest — `claimIndex` stores `refuted` off this
  // answer, and `refutedTwinsOf` carries it to matching claims in OTHER
  // explorations, so a result about wording that is gone marked strangers.
  if (n.outcome?.bearing === 'refuted' && outcomeLive(n)) return 'refuted'
  if (n.verification?.reading === 'refuted' && verificationLive(n)) return 'refuted'
  return kindChannelOf(n)
}

/**
 * The channel a node settles into. First match wins.
 *
 *  1. outcome-refuted — an observation happened. It outranks a source, because a
 *     source only *says*; and it outranks `terminal`, because `terminal` is the
 *     model's own judgement that a path is exhausted, and the tool's most
 *     valuable statement must not be erasable by that.
 *  2. verification-refuted, still live — a page the model opened says otherwise.
 *  3. any outcome, any bearing — a node someone actually tested never reads as
 *     dead. `dead` is a filterable channel, so without this one switch in the
 *     sidebar would hide the only measured thing in the exploration.
 *  4. terminal → dead. Unchanged and non-negotiable: an exhausted path must not
 *     compete for attention with a live one.
 *  5. the kind rules.
 *
 * There is no `contested` branch. Divergence is form, read from `panelSplit()`.
 */
export function channelOf(n: ReasonNode): Channel {
  const live = liveChannelOf(n)
  if (live === 'refuted') return 'refuted'
  if (n.outcome) return live
  return n.terminal ? 'dead' : live
}
